import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { matrixToPermissionStrings } from '@evi/shared';
import {
  ArrowLeft, Plus, Plug, Car, Pencil, Trash2, Download, CreditCard,
  Truck, Users, Radio, ListChecks, Wallet, Shield, Loader2, AlertTriangle,
  LayoutDashboard, Battery, Activity, Zap, UsersRound, CircuitBoard,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  fetchFleet, patchFleet, regenerateFleetInviteCode,
  fetchFleetDrivers, addFleetDriver, removeFleetDriver,
  fetchFleetChargers, addFleetCharger, removeFleetCharger,
  fetchFleetCircuits, createFleetCircuit, updateFleetCircuit, deleteFleetCircuit,
  assignChargerToCircuit, unassignChargerFromCircuit,
  enableFleetDlm, getDlmAddonRequired, getDisplayErrorMessage, type DlmAddonRequired,
  fetchChargers, type Charger,
  fetchFleetVehicles, createFleetVehicle, updateFleetVehicle, deleteFleetVehicle,
  fetchFleetKeyfobs, issueFleetKeyfob, patchFleetKeyfob,
  fetchFleetAutochargeCredentials, fetchFleetUnknownVehicleIds, issueFleetAutochargeCredential, releaseFleetAutochargeCredential,
  fetchFleetKeyfobPrice, getFleetKeyfobBillingBlock,
  type FleetKeyfobPrice, type FleetKeyfobCharge, type FleetKeyfobBillingBlock,
  fetchFleetSessions, fleetSessionsCsvPath,
  fetchFleetBilling, fetchFleetStats,
  switchFleetCadence, cancelFleetSubscription,
  reactivateFleetSubscription, startFleetPaymentMethodUpdate,
  confirmFleetPaymentMethod, type FleetPaymentMethodSetup,
  fetchFleetAdmins, addFleetAdmin, removeFleetAdmin,
  bulkUploadDrivers, bulkUploadKeyfobs,
  inviteFleetDriverByEmail,
  formatCents,
  fetchAdminGroups, fetchAdminGroup, createAdminGroup, updateAdminGroup, deleteAdminGroup,
  type FleetSummary, type FleetDriverRow, type FleetKeyfobRow, type FleetChargerRow,
  type FleetCircuitRow, type CircuitUpsertBody, type BreakerTripClass,
  type FleetVehicleRow, type VehicleUpsertBody,
  type VehicleOnboardPhases, type VehicleConnectorStandard,
  type FleetSessionRow, type FleetAdminRow,
  type FleetAutochargeCredentialRow, type FleetUnknownVehicleIdRow,
  type BulkDriverUploadSummary, type BulkKeyfobUploadSummary,
  type FleetStatsPerDriver,
  type GroupDetailView,
} from '@/lib/api';
import { Copy, RefreshCw, Upload, Mail, Link2, Share2, Check } from 'lucide-react';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { DriverCell } from '@/components/sessions/DriverCell';
import { UserTypeahead } from '@/components/ui/UserTypeahead';
import { CardSetupModal } from '@/components/billing/CardSetupModal';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthedDownload } from '@/hooks/useAuthedDownload';
import formStyles from '@/components/ui/Form.module.css';
import styles from './FleetDetailPage.module.css';
import { Switch } from '@/components/ui/Switch';
import { PLAN_PRICE_UNCONFIRMED, dlmAddonPriceLine, planCost, type PlanPricePair } from '@/lib/planPrices';

/**
 * Fleet detail (Phase 2 fleet platform). Tabs:
 *   Dashboard · Drivers · Chargers · Circuits · Vehicles · Keyfobs ·
 *   Groups · Sessions · Billing · Admins
 *
 * All sub-resources gate server-side via requireFleetScope —
 * tenant_admin / super_admin see everything in their tenant;
 * fleet_admin sees only their assigned fleet's data.
 *
 * The Dashboard tab doubles as the fleet_admin's home: they hold no
 * Dashboard:read bit and resolveDashboardScope has no fleet branch
 * (see docs/tickets/fleet-admin-dashboard-scope.md), so the operator
 * dashboard is not theirs. App.tsx lands a single-fleet operator here
 * directly; FleetsListPage forwards them here when the list resolves.
 */

type TabKey = 'overview' | 'drivers' | 'chargers' | 'circuits' | 'vehicles' | 'keyfobs' | 'autocharge' | 'groups' | 'sessions' | 'billing' | 'admins';

interface FleetDetailPageProps {
  fleetId: string;
  onNavigate: (path: string) => void;
  /** Which tab the PATH selected. App.tsx maps /fleet/drivers and
   *  friends onto this; absent means the plain /fleets/:id URL, which
   *  opens on the home tab. */
  initialTab?: TabKey;
}

export function FleetDetailPage({ fleetId, onNavigate, initialTab }: FleetDetailPageProps) {
  // Same ['fleets'] cache App.tsx keeps warm for fleet_admin (their landing
  // rule). Exactly one fleet means /fleets forwards straight back here.
  const viewerRole = useAuthStore((s) => s.user?.role);
  const cachedFleets = useQueryClient().getQueryData<Array<{ _id: string }>>(['fleets']);
  const singleFleetOperator = viewerRole === 'fleet_admin'
    && Array.isArray(cachedFleets) && cachedFleets.length === 1;
  const fleetQ = useQuery({
    queryKey: ['fleet', fleetId],
    queryFn: () => fetchFleet(fleetId),
  });
  // THE TAB IS ADDRESSABLE.
  //
  // It was `useState<TabKey>('overview')` with nothing reading a URL,
  // which is why the framework's whole Fleet menu — Drivers, Keyfobs
  // and cars, Sessions, Money, Settings — could not become sidebar
  // items: a nav leaf needs an address, and these eleven tabs had one
  // address between them.
  //
  // The tab now arrives as a PROP, driven by the path (/fleet/drivers
  // and friends in App.tsx). A path rather than a query string because
  // this app's router keys on `window.location.pathname` — a
  // query-only navigation would not re-render at all.
  const [tab, setTab] = useState<TabKey>(initialTab ?? 'overview');
  // Follow a nav click that changed the path while this page stays
  // mounted.
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  useRecentRoute(fleetQ.data?.name ?? 'Fleet detail');

  if (fleetQ.isLoading) {
    return (
      <div style={{ padding: 28 }}>
        <Loader2 size={16} className="spin" /> Loading fleet…
      </div>
    );
  }
  if (fleetQ.isError || !fleetQ.data) {
    return (
      <div style={{ padding: 28 }}>
        <AlertTriangle size={16} /> Could not load fleet.
        <button type="button" onClick={() => onNavigate('/fleets')} style={{ marginLeft: 8 }}>
          Back to fleets
        </button>
      </div>
    );
  }
  const fleet = fleetQ.data;
  const tabs = [
  // The tab id stays 'overview' — nothing deep-links to it, but
  // keeping the key means no state migration for open sessions.
  ['overview', 'Dashboard', LayoutDashboard],
  ['drivers', 'Drivers', Users],
  ['chargers', 'Chargers', Plug],
  ['circuits', 'Circuits', CircuitBoard],
  ['vehicles', 'Vehicles', Car],
  ['keyfobs', 'Keyfobs', Radio],
  ['autocharge', 'Plug & charge', Zap],
  // Groups is OPERATOR-only: GET /admin/groups gates on
  // Group:read (groups.ts:221) and the fleet_admin matrix does
  // not grant it, so for a fleet admin this tab was a 403 with
  // a label on it. Hidden rather than left to fail.
  ...(viewerRole === 'fleet_admin' ? [] : [['groups', 'Groups', UsersRound] as const]),
  ['sessions', 'Sessions', ListChecks],
  ['billing', 'Billing', Wallet],
  ['admins', 'Admins', Shield],
] as const;

  return (
    <div className={styles.page}>
      {/* A single-fleet Fleet Operator is forwarded from /fleets straight
          back here, so for them "Back to fleets" is a loop that only
          leaves a dead history entry. Hide it; the nav leaves are their
          way around. */}
      {!singleFleetOperator && (
        <button
          type="button"
          onClick={() => onNavigate('/fleets')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 0', background: 'transparent', border: 'none',
            color: 'var(--color-tx-m)', cursor: 'pointer', fontSize: 12, marginBottom: 12,
          }}
        >
          <ArrowLeft size={14} /> Back to fleets
        </button>
      )}

      <header className={styles.header} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 12%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--evi-accent, #2ECC71)',
          }}>
            <Truck size={18} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>{fleet.name}</h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
              {fleet.billing_email} · {fleet.is_active ? 'Active' : 'Inactive'}
            </p>
          </div>
        </div>
        <FleetEditButton fleet={fleet} />
      </header>

      {/* Phase 3a — invite code + email domain summary card */}
      <FleetInvitePanel fleet={fleet} />

      {/* Tab bar */}
      <label className={styles.mobileSection}>Fleet section
        <select value={tab} onChange={(event) => setTab(event.target.value as TabKey)}>
          {tabs.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
      </label>
      <div className={styles.tabs} style={{
        gap: 4, marginBottom: 16,
        borderBottom: '1px solid var(--color-brd, var(--evi-border))',
      }}>
        {tabs.map(([k, label, Icon]) => {
          const active = tab === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: active
                  ? '2px solid var(--evi-accent, #2ECC71)'
                  : '2px solid transparent',
                color: active ? 'var(--color-tx)' : 'var(--color-tx-m)',
                fontSize: 13, fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <OverviewTab fleetId={fleetId} />}
      {tab === 'drivers' && <DriversTab fleetId={fleetId} />}
      {tab === 'chargers' && <ChargersTab fleetId={fleetId} />}
      {tab === 'circuits' && <CircuitsTab fleetId={fleetId} onGoToBilling={() => setTab('billing')} />}
      {tab === 'vehicles' && <VehiclesTab fleetId={fleetId} />}
      {tab === 'keyfobs' && <KeyfobsTab fleetId={fleetId} onGoToBilling={() => setTab('billing')} />}
      {tab === 'autocharge' && <FleetAutochargeTab fleetId={fleetId} />}
      {tab === 'groups' && <GroupsTab fleetId={fleetId} />}
      {tab === 'sessions' && <SessionsTab fleetId={fleetId} />}
      {tab === 'billing' && <BillingTab fleetId={fleetId} fleet={fleet} />}
      {tab === 'admins' && <AdminsTab fleetId={fleetId} fleet={fleet} />}
    </div>
  );
}

// ── Dashboard tab (Phase 3b Item 4, promoted 2026-09-16) ───────────
//
// The per-fleet dashboard: a headline KPI row, the sessions-over-time
// chart (recharts) beside the top-drivers leaderboard, then the full
// per-driver breakdown. Everything comes from GET /admin/fleets/:id/stats
// — no second endpoint. The `?days=` window (default 30, the most
// common ops view) drives the series and the per-driver rows.
//
// The route's total_sessions / total_kwh / total_cost_cents are ALL-
// TIME, not windowed. A dashboard headline has to answer "what did
// this fleet do in the range I picked", so the in-range totals are
// summed client-side from per_driver: the route buckets every windowed
// session into exactly one row (the null-user bucket included), so the
// sums equal the windowed totals. All-time stays as each card's sub-line.

function OverviewTab({ fleetId }: { fleetId: string }) {
  const [days, setDays] = useState<number>(30);
  const statsQ = useQuery({
    queryKey: ['fleet-stats', fleetId, days],
    queryFn: () => fetchFleetStats(fleetId, days),
  });

  if (statsQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--color-tx-m)' }}>
        <Loader2 size={14} className="spin" /> Loading dashboard…
      </div>
    );
  }
  if (statsQ.isError || !statsQ.data) {
    return (
      <div style={{ padding: 24, color: 'var(--color-tx-m)' }}>
        <AlertTriangle size={14} /> Could not load the dashboard.
      </div>
    );
  }
  const s = statsQ.data;
  const inRange = s.per_driver.reduce(
    (acc, d) => ({
      sessions: acc.sessions + d.sessions_count,
      kwh: acc.kwh + d.energy_kwh,
      cents: acc.cents + d.cost_cents,
    }),
    { sessions: 0, kwh: 0, cents: 0 },
  );
  const rangeLabel = `${s.range_days}d`;

  const panelStyle = {
    padding: 16,
    background: 'var(--color-sf2, #1a1a1a)',
    border: '1px solid var(--color-brd, var(--evi-border))',
    borderRadius: 10,
    minWidth: 0,
  } as const;
  const panelTitleStyle = {
    margin: 0, fontSize: 11, letterSpacing: '1px',
    textTransform: 'uppercase', color: 'var(--color-tx-m)', marginBottom: 8,
  } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Title row + range picker */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 8, flexWrap: 'wrap',
      }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Last {s.range_days} days</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--color-tx-m)' }}>Range</span>
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid var(--color-brd, var(--evi-border))',
                background: days === n
                  ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 14%, transparent)'
                  : 'transparent',
                color: days === n ? 'var(--evi-accent, #2ECC71)' : 'var(--color-tx-m)',
                cursor: 'pointer',
              }}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {/* Headline KPI row. Drivers + keyfobs are current counts; the
          three usage cards are in-range with all-time underneath. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
        gap: 12,
      }}>
        <KpiCard
          icon={<Users size={14} />}
          label="Active drivers"
          value={s.active_drivers.toLocaleString()}
        />
        <KpiCard
          icon={<Radio size={14} />}
          label="Active keyfobs"
          value={s.active_keyfobs.toLocaleString()}
        />
        <KpiCard
          icon={<Activity size={14} />}
          label={`Sessions · ${rangeLabel}`}
          value={inRange.sessions.toLocaleString()}
          sub={`All time ${s.total_sessions.toLocaleString()}`}
        />
        <KpiCard
          icon={<Battery size={14} />}
          label={`kWh · ${rangeLabel}`}
          value={inRange.kwh.toFixed(1)}
          sub={`All time ${s.total_kwh.toFixed(1)}`}
        />
        <KpiCard
          icon={<Zap size={14} />}
          label={`Spend · ${rangeLabel}`}
          value={formatCents(inRange.cents)}
          sub={`All time ${formatCents(s.total_cost_cents)}`}
        />
      </div>

      {/* Chart beside the leaderboard on wide screens; they stack once
          the viewport can't fit two 320px columns. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 12,
      }}>
        {/* Sessions over time */}
        <section style={panelStyle}>
          <p style={panelTitleStyle}>
            Sessions over time
          </p>
          {s.sessions_by_day.length === 0 ? (
            <p style={{ color: 'var(--color-tx-m)', fontSize: 13 }}>
              No sessions in this window.
            </p>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={s.sessions_by_day} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
                  <CartesianGrid stroke="var(--color-brd, #222)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="var(--color-tx-m)" fontSize={11} />
                  <YAxis stroke="var(--color-tx-m)" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-sf, #111)',
                      border: '1px solid var(--color-brd, #333)',
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="var(--evi-accent, #2ECC71)"
                    strokeWidth={2}
                    dot={false}
                    name="Sessions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Top drivers leaderboard */}
        <section style={panelStyle}>
          <p style={{ ...panelTitleStyle, marginBottom: 10 }}>
            Top drivers
          </p>
          {s.top_drivers.length === 0 ? (
            <p style={{ color: 'var(--color-tx-m)', fontSize: 13 }}>
              No driver activity in this window.
            </p>
          ) : (
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {s.top_drivers.map((d, idx) => (
                <li
                  key={d.user_id ?? idx}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0',
                    borderBottom: idx === s.top_drivers.length - 1
                      ? 'none'
                      : '1px solid var(--color-brd, var(--evi-border))',
                  }}
                >
                  <span style={{
                    fontSize: 11, color: 'var(--color-tx-m)',
                    width: 18, textAlign: 'center',
                  }}>
                    #{idx + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                      {d.name ?? d.email ?? '(unknown)'}
                    </p>
                    {d.name && d.email && (
                      <p style={{ margin: 0, fontSize: 11, color: 'var(--color-tx-m)' }}>
                        {d.email}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {d.energy_kwh.toFixed(1)} kWh
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-tx-m)', fontVariantNumeric: 'tabular-nums' }}>
                      {d.sessions_count} sessions · {formatCents(d.cost_cents)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {/* Per-driver table */}
      <section>
        <p style={{
          margin: 0, fontSize: 11, letterSpacing: '1px',
          textTransform: 'uppercase', color: 'var(--color-tx-m)', marginBottom: 8,
        }}>
          Per-driver breakdown
        </p>
        <PerDriverTable rows={s.per_driver} />
      </section>
    </div>
  );
}

function KpiCard({
  icon, label, value, sub,
}: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div style={{
      padding: '14px 16px',
      background: 'var(--color-sf2, #1a1a1a)',
      border: '1px solid var(--color-brd, var(--evi-border))',
      borderRadius: 10,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10, letterSpacing: '1px',
        textTransform: 'uppercase', color: 'var(--color-tx-m)',
      }}>
        {icon}
        <span>{label}</span>
      </div>
      <p style={{
        margin: '6px 0 0', fontSize: 22, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </p>
      {sub && (
        <p style={{
          margin: '2px 0 0', fontSize: 11, color: 'var(--color-tx-m)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function PerDriverTable({ rows }: { rows: FleetStatsPerDriver[] }) {
  const columns: Column<FleetStatsPerDriver>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Driver',
      mobile: 'primary',
      cell: (r) => (
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
            {r.name ?? r.email ?? '(unknown)'}
          </p>
          {r.name && r.email && (
            <p style={{ margin: 0, fontSize: 11, color: 'var(--color-tx-m)' }}>
              {r.email}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'sessions_count',
      header: 'Sessions',
      align: 'right',
      cell: (r) => r.sessions_count.toString(),
    },
    {
      key: 'energy_kwh',
      header: 'kWh',
      align: 'right',
      cell: (r) => r.energy_kwh.toFixed(1),
    },
    {
      key: 'cost_cents',
      header: 'Cost',
      align: 'right',
      cell: (r) => formatCents(r.cost_cents),
    },
    {
      key: 'last_session_at',
      header: 'Last session',
      cell: (r) =>
        r.last_session_at
          ? new Date(r.last_session_at).toLocaleDateString()
          : '—',
    },
  ], []);

  return (
    <ResponsiveTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.user_id ?? `${r.email ?? 'anon'}-${r.last_session_at}`}
      emptyMessage="No driver activity in this window."
    />
  );
}

// ── Invite-code + email-domain panel (Phase 3a) ──────────────────

/**
 * Invite code card. Beyond the bare code it now carries the server-
 * composed `/join-fleet?code=` link (copy + native share) and a QR of
 * that link — the same three affordances the PWA's ShareChargerSection
 * gives a charger owner, so an operator can print the QR for a depot
 * wall or text the link instead of dictating eight base32 characters.
 * The URL and code stay in lockstep through Regenerate: both come back
 * from the same response, so the panel never shows a link for a code
 * that just stopped working.
 */
function FleetInvitePanel({ fleet }: { fleet: FleetSummary }) {
  const qc = useQueryClient();
  const [code, setCode] = useState(fleet.invite_code);
  const [joinUrl, setJoinUrl] = useState(fleet.join_url);
  const [copied, setCopied] = useState<'code' | 'url' | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const regen = useMutation({
    mutationFn: () => regenerateFleetInviteCode(fleet._id),
    onSuccess: (data) => {
      setCode(data.invite_code);
      setJoinUrl(data.join_url);
      void qc.invalidateQueries({ queryKey: ['fleet', fleet._id] });
    },
  });

  // QR renders client-side via the same `qrcode` lib qr-generator.ts
  // uses for charger stickers; dynamically imported so the admin route
  // chunk doesn't pay for it on fleets that never open this page. The
  // encoded URL is exactly the copyable link — nothing extra to leak.
  useEffect(() => {
    if (!joinUrl) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void import('qrcode')
      .then(({ default: QRCode }) => QRCode.toDataURL(joinUrl, { width: 480, margin: 1 }))
      .then((dataUrl) => { if (!cancelled) setQrDataUrl(dataUrl); })
      .catch(() => { /* QR is progressive enhancement — the code + link rows stand alone. */ });
    return () => { cancelled = true; };
  }, [joinUrl]);

  const flashCopied = (which: 'code' | 'url') => {
    setCopied(which);
    setTimeout(() => setCopied((cur) => (cur === which ? null : cur)), 1500);
  };
  const handleCopy = () => {
    if (!code) return;
    void navigator.clipboard.writeText(code).then(() => flashCopied('code'));
  };
  const handleCopyUrl = () => {
    if (!joinUrl) return;
    void navigator.clipboard.writeText(joinUrl).then(() => flashCopied('url'));
  };
  const handleShare = async () => {
    if (!joinUrl) return;
    try {
      await navigator.share({ title: `Join ${fleet.name} on EV Initiative`, url: joinUrl });
    } catch {
      // Dismissed the share sheet — nothing to do.
    }
  };
  const iconBtnStyle = {
    background: 'transparent', border: 'none',
    color: 'var(--color-tx-m)', cursor: 'pointer', padding: 2,
  } as const;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 12,
        marginBottom: 20,
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--color-sf2, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 10,
        }}
      >
        <p style={{
          margin: 0, fontSize: 10, letterSpacing: '1.2px',
          textTransform: 'uppercase', color: 'var(--color-tx-m)',
        }}>
          Invite code
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <code style={{
            fontSize: 18, fontWeight: 600, letterSpacing: '2px',
            fontFamily: 'var(--font-mono, ui-monospace)',
          }}>
            {code ?? '—'}
          </code>
          {code && (
            <button
              type="button"
              onClick={handleCopy}
              title="Copy code"
              style={iconBtnStyle}
            >
              <Copy size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirm('Regenerate invite code? The old code and link stop working immediately.')) {
                regen.mutate();
              }
            }}
            disabled={regen.isPending}
            title="Regenerate"
            style={iconBtnStyle}
          >
            <RefreshCw size={14} />
          </button>
          {copied === 'code' && <span style={{ fontSize: 11, color: 'var(--evi-accent, #2ECC71)' }}>Copied</span>}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-tx-m)' }}>
          Drivers enter this in the app to join the fleet, or open the link below.
          They need an account in this building first; a driver in another
          building joins it with the building code, then uses this one.
        </p>

        {joinUrl && (
          <>
            {/* Shareable link row. Same shape as the PWA's charger share
                row: URL in a bordered well, then Copy + (when the browser
                offers a share sheet) Share. Word-break keeps a long stage
                origin from overflowing the 260px card column. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
              <Link2 size={12} style={{ color: 'var(--color-tx-m)', flexShrink: 0 }} aria-hidden />
              <p style={{
                margin: 0, fontSize: 10, letterSpacing: '1.2px',
                textTransform: 'uppercase', color: 'var(--color-tx-m)',
              }}>
                Join link
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{
                flex: '1 1 100%', fontSize: 12, color: 'var(--color-tx-m)',
                wordBreak: 'break-all', padding: '8px 10px',
                border: '1px solid var(--color-brd, var(--evi-border))',
                borderRadius: 8, background: 'var(--color-bg, #0a0a0a)',
              }}>
                {joinUrl}
              </span>
              <button
                type="button"
                onClick={handleCopyUrl}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  borderRadius: 999, cursor: 'pointer',
                  border: '1px solid var(--color-brd, var(--evi-border))',
                  background: 'transparent', color: 'var(--color-tx)',
                }}
              >
                {copied === 'url' ? <Check size={13} /> : <Copy size={13} />}
                {copied === 'url' ? 'Copied' : 'Copy link'}
              </button>
              {typeof navigator.share === 'function' && (
                <button
                  type="button"
                  onClick={() => { void handleShare(); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    borderRadius: 999, cursor: 'pointer',
                    border: '1px solid var(--color-brd, var(--evi-border))',
                    background: 'transparent', color: 'var(--color-tx)',
                  }}
                >
                  <Share2 size={13} /> Share
                </button>
              )}
            </div>
            {qrDataUrl && (
              // White backing keeps the QR scannable on the dark admin theme.
              <div style={{
                margin: '12px auto 0', width: 'fit-content', padding: 10,
                background: '#fff', borderRadius: 12, lineHeight: 0,
                border: '1px solid var(--color-brd, var(--evi-border))',
              }}>
                <img
                  src={qrDataUrl}
                  alt={`QR code for the ${fleet.name} join link`}
                  style={{ width: 168, height: 168, display: 'block' }}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--color-sf2, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 10,
        }}
      >
        <p style={{
          margin: 0, fontSize: 10, letterSpacing: '1.2px',
          textTransform: 'uppercase', color: 'var(--color-tx-m)',
        }}>
          Email domain
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 14, fontWeight: 600 }}>
          {fleet.email_allowlist_domain ?? <em style={{ color: 'var(--color-tx-m)', fontWeight: 400 }}>None set</em>}
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-tx-m)' }}>
          Drivers who sign up with this email domain join the fleet after they verify their
          email by signing in with an emailed link. Personal email providers (gmail.com and
          similar) can't be used.
        </p>
      </div>
    </div>
  );
}

/**
 * The permission string `Fleet:update` emits, derived from the shared
 * resource map rather than written out, so the admin's gate cannot
 * drift from the API's. The API derives the same constant in
 * apps/api/src/lib/fleetPatchFields.ts.
 */
const FLEET_WRITE_PERMISSION: string = matrixToPermissionStrings([{
  resource: 'Fleet', create: false, read: false, update: true, delete: false,
}] as never)[0] ?? 'fleet:write';

// ── Edit fleet button (inline rename / activate-toggle) ──────────

function FleetEditButton({ fleet }: { fleet: FleetSummary }) {
  const qc = useQueryClient();
  // THE FORM IS SPLIT BY WHO OWNS THE FIELD (Warren's ruling, 2026-09-23).
  //
  // This button sits in the page header, so it renders on all seven
  // leaves of the Fleet menu. It used to 403 for a fleet admin
  // everywhere: PATCH /admin/fleets/:id was one Tenant:update gate, and
  // no fleet_admin matrix grants Tenant.
  //
  // The route now classifies its body (api lib/fleetPatchFields.ts).
  // name + billing_email need Fleet:update, which the fleet_admin
  // matrix now carries. The other four still need Tenant:update:
  // requires_driver_payment_method (fleet or driver pays — a commercial
  // term), default_stripe_payment_method_id, email_allowlist_domain
  // (anyone holding the domain can self-join) and is_active.
  //
  // ⚠️ THE BODY MUST MATCH THE PERMISSION, NOT JUST THE FORM. This
  // mutation used to send all five fields on every save regardless of
  // what changed, so a fleet admin renaming their fleet would still
  // have posted is_active and tripped the operator gate. It now sends
  // only the fields this viewer may write.
  //
  // The four operator fields are shown read-only with a sentence rather
  // than hidden — the Site Settings bank-account treatment.
  const viewerRole = useAuthStore((s) => s.user?.role);
  const isFleetAdmin = viewerRole === 'fleet_admin';

  // GATE THE AFFORDANCE ON THE CAPABILITY, NOT THE ROLE.
  //
  // The route needs Fleet:update (or Tenant:update). A fleet admin only
  // holds Fleet:update once seed:rbac-templates has been re-run on that
  // stage — and the Api deploy and the reseed are two separate steps in
  // the cutover runbook. Keying this button on `role === 'fleet_admin'`
  // would put a brand-new Edit button in front of the one persona the
  // ruling is for during that window, and every Save would 403. That is
  // precisely the "don't offer a control that fails" rule this whole
  // change exists to honour, so the button keys on the live permission
  // set from /auth/me instead and falls back to the sentence.
  //
  // Only the fleet-admin branch is capability-checked; every other
  // persona sees exactly what it saw before.
  const viewerPermissions = useAuthStore((s) => s.user?.permissions);
  const canWriteFleetFields = !isFleetAdmin
    || (viewerPermissions ?? []).includes(FLEET_WRITE_PERMISSION);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(fleet.name);
  const [email, setEmail] = useState(fleet.billing_email);
  const [isActive, setIsActive] = useState(fleet.is_active);
  const [domain, setDomain] = useState(fleet.email_allowlist_domain ?? '');
  const [requiresPm, setRequiresPm] = useState(
    Boolean(fleet.requires_driver_payment_method),
  );
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => patchFleet(
      fleet._id,
      // Fleet:update fields only for a fleet admin. Sending an
      // operator-only key — even unchanged, even `false` — makes the
      // route demand Tenant:update and the whole save 403s.
      isFleetAdmin
        ? {
          name: name.trim(),
          billing_email: email.trim(),
        }
        : {
          name: name.trim(),
          billing_email: email.trim(),
          is_active: isActive,
          email_allowlist_domain: domain.trim() === '' ? null : domain.trim(),
          requires_driver_payment_method: requiresPm,
        },
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet', fleet._id] });
      void qc.invalidateQueries({ queryKey: ['fleets'] });
      setOpen(false);
    },
    // The server's reason, not the bare HTTP line. This save now has
    // two failure modes worth reading — a 403 when the grant has not
    // landed, and the 409 when another fleet already claims the email
    // domain — and "Request failed with status code 403" tells the
    // reader neither.
    onError: async (e: Error) => setErr(
      await getDisplayErrorMessage(e, e.message || 'Save failed'),
    ),
  });

  /** Refill from the fleet each time the modal opens.
   *
   *  The field state initialises once at mount and the page outlives
   *  it: this component sits in the header of a tab set the user moves
   *  around for minutes, and the fleet query refetches on focus. An
   *  operator who opened the page, had another admin change a toggle,
   *  then opened Edit to fix a typo would have saved the stale toggle
   *  back — the operator branch posts all five fields, so a field they
   *  never looked at silently reverts. */
  const openModal = (): void => {
    setName(fleet.name);
    setEmail(fleet.billing_email);
    setIsActive(fleet.is_active);
    setDomain(fleet.email_allowlist_domain ?? '');
    setRequiresPm(Boolean(fleet.requires_driver_payment_method));
    setErr(null);
    setOpen(true);
  };

  // The deploy-before-reseed window: the persona is right but the grant
  // has not landed yet, so say so rather than show a button that 403s.
  if (!canWriteFleetFields) {
    return (
      <span style={{
        fontSize: 11, color: 'var(--color-tx-m)', textAlign: 'right',
        maxWidth: 220, lineHeight: 1.4,
      }}>
        Your operator sets the fleet name and billing email. Ask them to
        change these.
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        style={{
          padding: '6px 12px', background: 'transparent',
          color: 'var(--color-tx)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 8, fontSize: 12, cursor: 'pointer',
        }}
      >
        Edit
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(440px, 92vw)',
              background: 'var(--color-sf, #1a1a1a)',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 12, padding: 20,
            }}
          >
            <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>Edit fleet</h2>
            <label className={formStyles.label}>Name</label>
            <input className={formStyles.input} value={name} onChange={(e) => setName(e.target.value)} />
            <label className={formStyles.label} style={{ marginTop: 10 }}>Billing email</label>
            <input className={formStyles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            {isFleetAdmin ? (
              // The four operator-owned terms are NOT repeated inside
              // this modal. They live together on the Settings page,
              // where the grouping and the one "ask your operator"
              // sentence are the actual message; a second copy here
              // would put the email domain on screen three times (the
              // invite panel already shows it in the header).
              <p style={{
                fontSize: 11, color: 'var(--color-tx-m)',
                marginTop: 12, lineHeight: 1.5,
              }}>
                Who pays for a session, the auto-join email domain and whether
                the fleet is active are set by your operator. You can see them
                on the Settings page.
              </p>
            ) : (
              <>
                <label className={formStyles.label} style={{ marginTop: 10 }}>Email domain (optional)</label>
                <input
                  className={formStyles.input}
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="acme.com"
                />
                <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 4, lineHeight: 1.5 }}>
                  Drivers who sign up with this email domain join your fleet after they verify their
                  email by signing in with an emailed link. Personal email providers (gmail.com and
                  similar) can't be used.
                </p>
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  label="Active (drivers' sessions bill to this fleet)"
                />
                {/* Phase 7 — driver-PM-required toggle. Drives the
                    secondary access gate at every session-creation
                    entry point (driver-start, Authorize, StartTransaction).
                    Default false preserves the v1 behavior. */}
                <Switch
                  checked={requiresPm}
                  onChange={(e) => setRequiresPm(e.target.checked)}
                  label={<>
                    Require driver payment method
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--color-tx-m)', marginTop: 2, lineHeight: 1.5 }}>
                      When on, drivers in this fleet must have a saved card on file
                      to start fleet-mode sessions. Use when the fleet covers some sessions
                      but drivers fund others (overage / personal usage).
                    </span>
                  </>}
                />
              </>
            )}
            {err && (
              <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
                {err}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setOpen(false)} disabled={mut.isPending}
                style={{
                  padding: '8px 14px', background: 'transparent',
                  border: '1px solid var(--color-brd, var(--evi-border))',
                  color: 'var(--color-tx)', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                }}>
                Cancel
              </button>
              <button type="button" onClick={() => { setErr(null); mut.mutate(); }}
                disabled={mut.isPending}
                style={{
                  padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)',
                  color: '#000', border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>
                {mut.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The four operator-owned PATCH fields, shown read-only on the Settings
 * page rather than hidden.
 *
 * Hiding them would make the fleet admin's settings look complete while
 * silently omitting the terms that decide who pays and who can join —
 * the same reasoning as the Site Settings bank-account row: state the
 * value, name who changes it, don't offer a control that fails.
 *
 * Three of the four (domain, active, driver-payment-method) are fields
 * of the operator's edit form. The fourth,
 * default_stripe_payment_method_id, never appeared on that form at all —
 * it exists only in the patchFleet body type and the route schema. It is
 * surfaced here anyway, pointing at the Money tab, because the question
 * "where is my card?" is the one a fleet admin will actually have. That
 * tab's capture modal posts to
 * /admin/fleets/:id/billing/update-payment-method, which carries no
 * matrix gate, so adding or replacing a card IS theirs to do — only the
 * operator's raw-id override is closed to them.
 */
function FleetOperatorOnlySettings({ fleet }: { fleet: FleetSummary }) {
  // Email domain is deliberately NOT a row here. FleetInvitePanel
  // prints it in the page header on every leaf, including this one, so
  // a row would put the same field on screen twice with no sign they
  // are the same thing. The sentence below still names it as the
  // operator's.
  const rows: Array<{ label: string; value: string }> = [
    {
      label: 'Status',
      value: fleet.is_active ? 'Active — sessions bill to this fleet' : 'Inactive',
    },
    {
      label: 'Driver payment method',
      value: fleet.requires_driver_payment_method
        ? 'Required for fleet-mode sessions'
        : 'Not required',
    },
    {
      label: 'Card on file',
      value: fleet.default_stripe_payment_method_id
        ? 'Set — change it on the Money tab'
        : 'None — add one on the Money tab',
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <p style={{
        margin: '0 0 8px', fontSize: 10, textTransform: 'uppercase',
        letterSpacing: '1.2px', color: 'var(--color-tx-m)',
      }}>
        Set by your operator
      </p>
      <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <dt style={{ fontSize: 12, color: 'var(--color-tx-m)' }}>{r.label}</dt>
            <dd style={{ margin: 0, fontSize: 12, textAlign: 'right' }}>{r.value}</dd>
          </div>
        ))}
      </dl>
      <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 10, lineHeight: 1.5 }}>
        Whether the fleet or the driver pays, which email domain joins
        automatically, and whether the fleet is active are commercial terms.
        Ask your operator to change them. You can add or replace the card
        yourself on the Money tab.
      </p>
    </div>
  );
}

// ── Drivers tab ────────────────────────────────────────────

function DriversTab({ fleetId }: { fleetId: string }) {
  const qc = useQueryClient();
  const driversQ = useQuery({
    queryKey: ['fleet-drivers', fleetId],
    queryFn: () => fetchFleetDrivers(fleetId),
  });
  const [addOpen, setAddOpen] = useState(false);
  const [inviteEmailOpen, setInviteEmailOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (uid: string) => addFleetDriver(fleetId, uid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet-drivers', fleetId] });
      setAddOpen(false);
      setNewUserId('');
      setErr(null);
    },
    onError: (e: Error) => setErr(e.message),
  });
  const remove = useMutation({
    mutationFn: (uid: string) => removeFleetDriver(fleetId, uid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-drivers', fleetId] }),
  });

  const columns: Column<FleetDriverRow>[] = [
    { key: 'name', header: 'Name', cell: (r) => r.name },
    { key: 'email', header: 'Email', cell: (r) => r.email },
    { key: 'total_sessions', header: 'Sessions', cell: (r) => r.total_sessions },
    { key: 'last_seen_at', header: 'Last seen', cell: (r) => {
      const seen = r.last_seen_at ?? r.last_login;
      return seen ? new Date(seen).toLocaleDateString() : 'Never';
    } },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <button
          type="button"
          aria-label="Remove driver"
          onClick={() => {
            if (confirm(`Remove ${r.name} from this fleet?`)) remove.mutate(r._id);
          }}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--evi-error, #ef4444)',
          }}
        >
          <Trash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Drivers ({driversQ.data?.length ?? 0})</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => setBulkOpen(true)} title="Bulk CSV upload"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'transparent',
              color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 8, fontSize: 12, cursor: 'pointer',
            }}>
            <Upload size={12} /> Bulk upload
          </button>
          <button type="button" onClick={() => setInviteEmailOpen(true)} title="Add by email"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'transparent',
              color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 8, fontSize: 12, cursor: 'pointer',
            }}>
            <Mail size={12} /> Add by email
          </button>
          <button type="button" onClick={() => setAddOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
            }}>
            <Plus size={12} /> Add by ID
          </button>
        </div>
      </div>

      {driversQ.isLoading && <p>Loading…</p>}
      {(driversQ.data ?? []).length === 0 && !driversQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No drivers attributed yet.</p>
      )}
      {(driversQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetDriverRow>
          rows={driversQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {addOpen && (
        <SimpleEntryModal
          title="Add driver"
          label="User ID (24-char ObjectId)"
          value={newUserId}
          onChange={setNewUserId}
          placeholder="507f1f77bcf86cd799439011"
          err={err}
          onClose={() => { setAddOpen(false); setErr(null); }}
          onSubmit={() => { setErr(null); add.mutate(newUserId.trim()); }}
          submitting={add.isPending}
        />
      )}

      {inviteEmailOpen && (
        <InviteByEmailModal
          fleetId={fleetId}
          onClose={() => setInviteEmailOpen(false)}
          // Refresh only: the modal stays open on its outcome panel and
          // its Done button closes it (same as BulkDriversModal below).
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-drivers', fleetId] });
          }}
        />
      )}
      {bulkOpen && (
        <BulkDriversModal
          fleetId={fleetId}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-drivers', fleetId] });
          }}
        />
      )}
    </div>
  );
}

// ── Chargers tab (Phase 1b) ─────────────────────────────────

function ChargersTab({ fleetId }: { fleetId: string }) {
  const qc = useQueryClient();
  const chargersQ = useQuery({
    queryKey: ['fleet-chargers', fleetId],
    queryFn: () => fetchFleetChargers(fleetId),
  });
  const [addOpen, setAddOpen] = useState(false);
  // Binding hardware to a fleet is an operator act (Charger:update on the
  // API since 2026-09-20 — Charger.fleet_id is the fleet tier's read
  // boundary). A fleet admin sees the roster, not the assign / unassign
  // controls; same hint the Admins tab gives.
  const isFleetAdmin = useAuthStore((s) => s.user?.role) === 'fleet_admin';

  const remove = useMutation({
    mutationFn: (cid: string) => removeFleetCharger(fleetId, cid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-chargers', fleetId] }),
  });

  const columns: Column<FleetChargerRow>[] = [
    { key: 'identity', header: 'Identity', cell: (r) => r.identity },
    { key: 'display_name', header: 'Name', cell: (r) => r.display_name ?? '—' },
    {
      key: 'is_online',
      header: 'Status',
      cell: (r) => r.is_online
        ? <span style={{ color: 'var(--evi-success, #10b981)' }}>Online</span>
        : <span style={{ color: 'var(--color-tx-m)' }}>Offline</span>,
    },
    {
      key: 'allow_mixed_use',
      header: 'Mixed-use',
      cell: (r) => r.allow_mixed_use === null ? 'Inherit' : r.allow_mixed_use ? 'Yes' : 'No',
    },
    ...(isFleetAdmin ? [] : [{
      key: 'actions',
      header: '',
      cell: (r: FleetChargerRow) => (
        <button
          type="button"
          aria-label="Unassign charger from fleet"
          onClick={() => {
            if (confirm(`Unassign ${r.identity} from this fleet?`)) remove.mutate(r._id);
          }}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--evi-error, #ef4444)',
          }}
        >
          <Trash2 size={14} />
        </button>
      ),
    } satisfies Column<FleetChargerRow>]),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Chargers ({chargersQ.data?.length ?? 0})</h3>
        {isFleetAdmin ? (
          <span style={{ fontSize: 11, color: 'var(--color-tx-m)' }}>
            Ask your operator to assign or remove chargers.
          </span>
        ) : (
          <button type="button" onClick={() => setAddOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
            }}>
            <Plus size={12} /> Assign charger
          </button>
        )}
      </div>

      {chargersQ.isLoading && <p>Loading…</p>}
      {(chargersQ.data ?? []).length === 0 && !chargersQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No chargers assigned to this fleet yet.</p>
      )}
      {(chargersQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetChargerRow>
          rows={chargersQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {addOpen && (
        <AssignChargerModal
          fleetId={fleetId}
          onClose={() => setAddOpen(false)}
          onAssigned={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-chargers', fleetId] });
            void qc.invalidateQueries({ queryKey: ['assignable-chargers'] });
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ── Assign-charger picker (Phase 1c) ────────────────────────
//
// Replaces the raw 24-char ObjectId input with a searchable picker. Candidate
// source is GET /chargers (fetchChargers), which scopes server-side by role:
// the tenant's chargers for super_admin / tenant_admin, empty for fleet_admin
// (clean empty state, not an error). Chargers already on a fleet (fleet_id
// set) are excluded to avoid a double-assign. Client-side filter on name +
// serial; a single page at the max limit (a tenant with >100 chargers would
// only surface the first 100 — server-side search is the future follow-up).

function AssignChargerModal({
  fleetId, onClose, onAssigned,
}: {
  fleetId: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [query, setQuery] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const chargersQ = useQuery({
    queryKey: ['assignable-chargers'],
    queryFn: () => fetchChargers({ limit: 100 }),
  });

  const add = useMutation({
    mutationFn: (chargerId: string) => addFleetCharger(fleetId, chargerId),
    onSuccess: () => onAssigned(),
    onError: (e: Error) => setErr(e.message || 'Failed to assign charger'),
  });

  const all = chargersQ.data?.data ?? [];
  const assignable = all.filter((c) => !c.fleet_id);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? assignable.filter((c) =>
        (c.display_name ?? '').toLowerCase().includes(q)
        || c.identity.toLowerCase().includes(q)
        || (c.serial_number ?? '').toLowerCase().includes(q))
    : assignable;
  const hiddenAssigned = all.length - assignable.length;

  const pick = (c: Charger): void => { setErr(null); add.mutate(c._id); };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(520px, 94vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--color-sf, #1a1a1a)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Assign charger</h2>

        <input className={formStyles.input} placeholder="Search by name or serial…" value={query}
          onChange={(e) => setQuery(e.target.value)} autoFocus disabled={add.isPending} />

        {err && (
          <div style={{ marginTop: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>{err}</div>
        )}

        <div style={{ marginTop: 10, overflowY: 'auto', minHeight: 120, maxHeight: 320, border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8 }}>
          {chargersQ.isLoading && <p style={{ padding: 14, margin: 0, color: 'var(--color-tx-m)', fontSize: 13 }}>Loading chargers…</p>}
          {chargersQ.isError && <p style={{ padding: 14, margin: 0, color: 'var(--evi-error, #ef4444)', fontSize: 13 }}>Couldn’t load chargers.</p>}
          {!chargersQ.isLoading && !chargersQ.isError && all.length === 0 && (
            <p style={{ padding: 14, margin: 0, color: 'var(--color-tx-m)', fontSize: 13 }}>No chargers available to assign.</p>
          )}
          {!chargersQ.isLoading && !chargersQ.isError && all.length > 0 && assignable.length === 0 && (
            <p style={{ padding: 14, margin: 0, color: 'var(--color-tx-m)', fontSize: 13 }}>All your chargers are already assigned to a fleet.</p>
          )}
          {!chargersQ.isLoading && assignable.length > 0 && filtered.length === 0 && (
            <p style={{ padding: 14, margin: 0, color: 'var(--color-tx-m)', fontSize: 13 }}>No charger matches “{query}”.</p>
          )}
          {filtered.map((c) => (
            <button key={c._id} type="button" disabled={add.isPending} onClick={() => pick(c)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                width: '100%', textAlign: 'left', padding: '10px 12px', background: 'transparent',
                border: 'none', borderTop: '1px solid var(--color-brd, var(--evi-border))',
                color: 'var(--color-tx)', cursor: add.isPending ? 'default' : 'pointer',
              }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.display_name ?? c.identity}</span>
                <code style={{ color: 'var(--color-tx-m)', fontSize: 11 }}>{c.identity}</code>
              </span>
              <span style={{ flexShrink: 0, fontSize: 11, color: c.is_online ? 'var(--evi-success, #10b981)' : 'var(--color-tx-m)' }}>
                {c.is_online ? '● online' : '○ offline'}
              </span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <span style={{ fontSize: 11, color: 'var(--color-tx-m)' }}>
            {add.isPending ? 'Assigning…' : hiddenAssigned > 0 ? `${hiddenAssigned} already on a fleet (hidden)` : ''}
          </span>
          <button type="button" onClick={onClose} disabled={add.isPending}
            style={{ padding: '8px 14px', background: 'transparent', color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vehicles tab (Phase 2a) ─────────────────────────────────

function VehiclesTab({ fleetId }: { fleetId: string }) {
  const qc = useQueryClient();
  const vehiclesQ = useQuery({
    queryKey: ['fleet-vehicles', fleetId],
    queryFn: () => fetchFleetVehicles(fleetId),
  });
  // Drivers power the primary-driver picker AND the name resolution in the
  // list (the API returns primary_driver_id raw).
  const driversQ = useQuery({
    queryKey: ['fleet-drivers', fleetId],
    queryFn: () => fetchFleetDrivers(fleetId),
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FleetVehicleRow | null>(null);

  const remove = useMutation({
    mutationFn: (vid: string) => deleteFleetVehicle(fleetId, vid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-vehicles', fleetId] }),
  });

  const driverLabel = (id: string | null): string => {
    if (!id) return '— Pool —';
    const d = (driversQ.data ?? []).find((x) => x._id === id);
    return d ? d.name : 'Unknown driver';
  };

  const columns: Column<FleetVehicleRow>[] = [
    { key: 'vin', header: 'VIN / Label', cell: (r) => r.label ? `${r.label} · ${r.vin}` : r.vin },
    { key: 'vehicle', header: 'Vehicle', cell: (r) => `${r.year} ${r.make} ${r.model}` },
    { key: 'battery', header: 'Battery', cell: (r) => `${r.battery_capacity_kwh} kWh` },
    { key: 'power', header: 'Max power', cell: (r) => `${r.max_charge_power_kw} kW` },
    { key: 'connector', header: 'Connector', cell: (r) => `${r.connector_standard} · ${r.onboard_phases}φ` },
    { key: 'driver', header: 'Primary driver', cell: (r) => driverLabel(r.primary_driver_id) },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            aria-label="Edit vehicle"
            onClick={() => { setEditing(r); setFormOpen(true); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-tx-m)' }}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            aria-label="Delete vehicle"
            onClick={() => {
              if (confirm(`Delete ${r.year} ${r.make} ${r.model}?`)) remove.mutate(r._id);
            }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-error, #ef4444)' }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Vehicles ({vehiclesQ.data?.length ?? 0})</h3>
        <button type="button" onClick={() => { setEditing(null); setFormOpen(true); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
            color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
            fontWeight: 600, cursor: 'pointer',
          }}>
          <Plus size={12} /> Add vehicle
        </button>
      </div>

      {vehiclesQ.isLoading && <p>Loading…</p>}
      {(vehiclesQ.data ?? []).length === 0 && !vehiclesQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No vehicles in this fleet yet.</p>
      )}
      {(vehiclesQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetVehicleRow>
          rows={vehiclesQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {formOpen && (
        <VehicleFormModal
          fleetId={fleetId}
          drivers={driversQ.data ?? []}
          editing={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-vehicles', fleetId] });
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function VehicleField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className={formStyles.label}>{label}</span>
      {children}
    </label>
  );
}

function VehicleFormModal({
  fleetId, drivers, editing, onClose, onDone,
}: {
  fleetId: string;
  drivers: FleetDriverRow[];
  editing: FleetVehicleRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [vin, setVin] = useState(editing?.vin ?? '');
  const [make, setMake] = useState(editing?.make ?? '');
  const [model, setModel] = useState(editing?.model ?? '');
  const [year, setYear] = useState(editing ? String(editing.year) : '');
  const [label, setLabel] = useState(editing?.label ?? '');
  const [plate, setPlate] = useState(editing?.license_plate ?? '');
  const [battery, setBattery] = useState(editing ? String(editing.battery_capacity_kwh) : '');
  const [power, setPower] = useState(editing ? String(editing.max_charge_power_kw) : '');
  const [current, setCurrent] = useState(editing ? String(editing.max_charge_current_a) : '');
  const [phases, setPhases] = useState<VehicleOnboardPhases>(editing?.onboard_phases ?? 3);
  const [connector, setConnector] = useState<VehicleConnectorStandard>(editing?.connector_standard ?? 'CCS');
  const [driverId, setDriverId] = useState(editing?.primary_driver_id ?? '');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (body: VehicleUpsertBody) =>
      editing ? updateFleetVehicle(fleetId, editing._id, body) : createFleetVehicle(fleetId, body),
    onSuccess: onDone,
    onError: (e: Error) => setErr(e.message || 'Failed to save vehicle'),
  });

  const submit = () => {
    setErr(null);
    const yr = Number(year);
    const bat = Number(battery);
    const pw = Number(power);
    const cur = Number(current);
    if (!vin.trim() || !make.trim() || !model.trim()) {
      setErr('VIN, make, and model are required.');
      return;
    }
    if (!Number.isInteger(yr) || yr < 1900 || yr > 2100) {
      setErr('Year must be a whole number between 1900 and 2100.');
      return;
    }
    if (!(bat > 0) || !(pw > 0) || !(cur > 0)) {
      setErr('Battery, max power, and max current must be positive numbers.');
      return;
    }
    mut.mutate({
      vin: vin.trim(),
      make: make.trim(),
      model: model.trim(),
      year: yr,
      label: label.trim() || null,
      license_plate: plate.trim() || null,
      battery_capacity_kwh: bat,
      max_charge_power_kw: pw,
      max_charge_current_a: cur,
      onboard_phases: phases,
      connector_standard: connector,
      primary_driver_id: driverId || null,
    });
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(540px, 94vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--color-sf, #1a1a1a)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{editing ? 'Edit vehicle' : 'Add vehicle'}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-pair-columns, 1fr 1fr)', gap: 10 }}>
          <VehicleField label="VIN"><input className={formStyles.input} value={vin} onChange={(e) => setVin(e.target.value)} placeholder="5YJ3E1EA7KF..." /></VehicleField>
          <VehicleField label="Label (optional)"><input className={formStyles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Van 12" /></VehicleField>
          <VehicleField label="Make"><input className={formStyles.input} value={make} onChange={(e) => setMake(e.target.value)} placeholder="Tesla" /></VehicleField>
          <VehicleField label="Model"><input className={formStyles.input} value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model 3" /></VehicleField>
          <VehicleField label="Year"><input className={formStyles.input} type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="2024" /></VehicleField>
          <VehicleField label="License plate (optional)"><input className={formStyles.input} value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="ABC-123" /></VehicleField>
          <VehicleField label="Battery (kWh)"><input className={formStyles.input} type="number" value={battery} onChange={(e) => setBattery(e.target.value)} placeholder="75" /></VehicleField>
          <VehicleField label="Max charge power (kW)"><input className={formStyles.input} type="number" value={power} onChange={(e) => setPower(e.target.value)} placeholder="11" /></VehicleField>
          <VehicleField label="Max charge current (A)"><input className={formStyles.input} type="number" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="48" /></VehicleField>
          <VehicleField label="Onboard phases">
            <select className={formStyles.select} value={phases} onChange={(e) => setPhases(Number(e.target.value) as VehicleOnboardPhases)}>
              <option value={1}>1-phase</option>
              <option value={3}>3-phase</option>
            </select>
          </VehicleField>
          <VehicleField label="Connector">
            <select className={formStyles.select} value={connector} onChange={(e) => setConnector(e.target.value as VehicleConnectorStandard)}>
              <option value="Type2">Type2 (AC)</option>
              <option value="CCS">CCS (DC)</option>
              <option value="CHAdeMO">CHAdeMO (DC)</option>
            </select>
          </VehicleField>
          <VehicleField label="Primary driver">
            <select className={formStyles.select} value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">— None (pool) —</option>
              {drivers.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
            </select>
          </VehicleField>
        </div>

        {err && (
          <div style={{ marginTop: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={mut.isPending}
            style={{ padding: '8px 14px', background: 'transparent', color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={submit} disabled={mut.isPending}
            style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {mut.isPending ? 'Saving…' : (editing ? 'Save changes' : 'Add vehicle')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Circuits tab (Phase 3c — DLM) ───────────────────────────
//
// Operator surface for the dynamic load management foundation: CRUD on a
// fleet's depot circuits + assign/unassign member chargers, plus read-only
// observability of what the 3b.2 control loop last pushed (dlm_last_cap_a /
// dlm_last_pushed_at, carried on the fleet-chargers list since Phase 3c).

const TRIP_CLASS_OPTIONS: BreakerTripClass[] = ['B', 'C', 'D'];

/** Recommended DLM ceiling — mirrors the backend's 0.8 * breaker_rating_a
 *  derate WARNING (guidance, never a hard cap). */
function derateCeiling(breakerRatingA: number): number {
  return Math.floor(0.8 * breakerRatingA);
}

function fmtCap(a: number | null): string {
  return a === null || a === undefined ? '—' : `${a} A`;
}

/** Compact "time since" for the loop's last push. */
function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

// onGoToBilling: the circuit-create paywall's "start a subscription" step
// lives on this page's Billing tab. The tab is local state (not URL), so
// the modal switches it in place rather than navigating to the
// subscribe_url the 402 carries — same idiom as KeyfobsTab's past-due prompt.
function CircuitsTab({ fleetId, onGoToBilling }: { fleetId: string; onGoToBilling: () => void }) {
  const qc = useQueryClient();
  const circuitsQ = useQuery({
    queryKey: ['fleet-circuits', fleetId],
    queryFn: () => fetchFleetCircuits(fleetId),
  });
  // Fleet chargers carry circuit_id + the loop's last-pushed cap (Phase 3c
  // projection) — powering membership, the assign picker, and observability.
  const chargersQ = useQuery({
    queryKey: ['fleet-chargers', fleetId],
    queryFn: () => fetchFleetChargers(fleetId),
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FleetCircuitRow | null>(null);
  const [managing, setManaging] = useState<FleetCircuitRow | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => deleteFleetCircuit(fleetId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet-circuits', fleetId] });
      void qc.invalidateQueries({ queryKey: ['fleet-chargers', fleetId] });
    },
  });

  const chargers = chargersQ.data ?? [];
  const capSum = (circuitId: string): number =>
    chargers
      .filter((c) => c.circuit_id === circuitId)
      .reduce((acc, c) => acc + (c.dlm_last_cap_a ?? 0), 0);

  const columns: Column<FleetCircuitRow>[] = [
    { key: 'name', header: 'Circuit', cell: (r) => r.name },
    { key: 'budget', header: 'DLM budget', cell: (r) => <code>{r.limit_a} A</code> },
    {
      key: 'breaker',
      header: 'Breaker',
      cell: (r) => <code>{r.breaker_rating_a} A · {r.breaker_trip_class}</code>,
    },
    {
      key: 'load',
      header: 'Loop load',
      cell: (r) => {
        const sum = capSum(r._id);
        return (
          <code style={{ color: sum > r.limit_a ? 'var(--evi-error, #ef4444)' : 'var(--color-tx)' }}>
            {sum} / {r.limit_a} A
          </code>
        );
      },
    },
    { key: 'chargers', header: 'Chargers', cell: (r) => String(r.charger_count) },
    {
      key: 'is_active',
      header: 'Loop',
      cell: (r) => (
        <span style={{
          padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
          background: r.is_active
            ? 'color-mix(in srgb, var(--evi-success, #10b981) 14%, transparent)'
            : 'color-mix(in srgb, var(--color-tx-m) 18%, transparent)',
          color: r.is_active ? 'var(--evi-success, #10b981)' : 'var(--color-tx-m)',
        }}>
          {r.is_active ? 'Managed' : 'Paused'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button type="button" aria-label="Manage chargers" onClick={() => setManaging(r)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-accent, #2ECC71)', fontSize: 12, fontWeight: 600 }}>
            Chargers
          </button>
          <button type="button" aria-label="Edit circuit" onClick={() => { setEditing(r); setFormOpen(true); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-tx-m)' }}>
            <Pencil size={14} />
          </button>
          <button type="button" aria-label="Delete circuit"
            onClick={() => { if (confirm(`Delete circuit "${r.name}"? Member chargers will be unassigned.`)) remove.mutate(r._id); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-error, #ef4444)' }}>
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Circuits ({circuitsQ.data?.length ?? 0})</h3>
        <button type="button" onClick={() => { setEditing(null); setFormOpen(true); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
            color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
            fontWeight: 600, cursor: 'pointer',
          }}>
          <Plus size={12} /> Add circuit
        </button>
      </div>
      <p style={{ color: 'var(--color-tx-m)', fontSize: 12, margin: '0 0 14px' }}>
        The control loop balances each <strong>Managed</strong> circuit's chargers against its amp budget.
        “Loop load” sums the caps the loop last pushed to member chargers.
      </p>

      {circuitsQ.isLoading && <p>Loading…</p>}
      {(circuitsQ.data ?? []).length === 0 && !circuitsQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No circuits in this fleet yet.</p>
      )}
      {(circuitsQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetCircuitRow>
          rows={circuitsQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {formOpen && (
        <CircuitFormModal
          fleetId={fleetId}
          editing={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-circuits', fleetId] });
            setFormOpen(false);
            setEditing(null);
          }}
          onGoToBilling={() => { setFormOpen(false); setEditing(null); onGoToBilling(); }}
        />
      )}

      {managing && (
        <CircuitChargersModal
          fleetId={fleetId}
          circuit={managing}
          chargers={chargers}
          onClose={() => setManaging(null)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-chargers', fleetId] });
            void qc.invalidateQueries({ queryKey: ['fleet-circuits', fleetId] });
          }}
        />
      )}
    </div>
  );
}

function CircuitFormModal({
  fleetId, editing, onClose, onDone, onGoToBilling,
}: {
  fleetId: string;
  editing: FleetCircuitRow | null;
  onClose: () => void;
  onDone: () => void;
  onGoToBilling: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [limit, setLimit] = useState(editing ? String(editing.limit_a) : '');
  const [breaker, setBreaker] = useState(editing ? String(editing.breaker_rating_a) : '');
  const [tripClass, setTripClass] = useState<BreakerTripClass>(editing?.breaker_trip_class ?? 'C');
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [err, setErr] = useState<string | null>(null);
  // 402 DLM_ADDON_REQUIRED from create (the DLM paywall): rendered as an
  // inline prompt — price, today's managed-charger count, "Add the DLM
  // add-on" — instead of the red error, because the fix is one click away
  // for a subscribed tenant_admin. can_enable is the server's whole
  // verdict: false for a fleet_admin (fleet DLM enable is tenant_admin /
  // super_admin only, and Warren kept it that way) AND for a fleet with no
  // live subscription (the enable route 402s SUBSCRIPTION_REQUIRED, since
  // DLM bills as an item on the subscription). Either way the panel shows
  // the server's hint with no enable button, plus a jump to the Billing
  // tab when the 402 carries a subscribe_url. lastBody is the create the
  // 402 interrupted, retried once the add-on is on. Edits never trip the
  // gate (it is on create only).
  const [addon, setAddon] = useState<DlmAddonRequired | null>(null);
  const [lastBody, setLastBody] = useState<CircuitUpsertBody | null>(null);

  const limitNum = Number(limit);
  const breakerNum = Number(breaker);
  // Live, NON-blocking derate guidance (mirrors the backend 0.8x warning).
  const derateWarn =
    breakerNum > 0 && limitNum > 0.8 * breakerNum
      ? `Budget (${limitNum} A) exceeds 80% of the breaker (${breakerNum} A). Recommended ceiling ${derateCeiling(breakerNum)} A — you can save anyway.`
      : null;

  const mut = useMutation({
    mutationFn: (body: CircuitUpsertBody) =>
      editing ? updateFleetCircuit(fleetId, editing._id, body) : createFleetCircuit(fleetId, body),
    onSuccess: () => onDone(),
    onError: async (e: Error) => {
      const required = await getDlmAddonRequired(e);
      if (required) { setAddon(required); return; }
      setErr(e.message || 'Failed to save circuit');
    },
  });

  // "Add the DLM add-on": enable on THIS fleet (POST /admin/fleets/:id/dlm/
  // enable — the fleet's own subscription carries the DLM item, not the
  // tenant's), then retry the create.
  const enableM = useMutation({
    mutationFn: () => enableFleetDlm(fleetId),
    onSuccess: () => {
      setAddon(null);
      if (lastBody) mut.mutate(lastBody);
    },
    onError: async (e: Error) => {
      setErr(await getDisplayErrorMessage(e, 'Could not add the DLM add-on.'));
    },
  });

  const submit = () => {
    setErr(null);
    setAddon(null);
    if (!name.trim()) { setErr('Name is required.'); return; }
    if (!(breakerNum > 0)) {
      setErr('Breaker rating must be a positive number.');
      return;
    }
    // Hard 6A floor — distinct from the soft derate WARNING above.
    if (!(limitNum >= 6)) {
      setErr('DLM budget must be at least 6A, the lowest current an EV will charge at.');
      return;
    }
    const body: CircuitUpsertBody = {
      name: name.trim(),
      limit_a: limitNum,
      breaker_rating_a: breakerNum,
      breaker_trip_class: tripClass,
      is_active: isActive,
    };
    setLastBody(body);
    mut.mutate(body);
  };

  // The fleet's cadence's load management price from its own Stripe Price,
  // exact cents, or "EV Initiative confirms the price" (Warren, 21 October
  // 2026, night).
  const perCharger = addon ? dlmAddonPriceLine(addon, formatCents) : '';

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(480px, 94vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--color-sf, #1a1a1a)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{editing ? 'Edit circuit' : 'Add circuit'}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-pair-columns, 1fr 1fr)', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <VehicleField label="Name"><input className={formStyles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Depot feed A" /></VehicleField>
          </div>
          <VehicleField label="DLM budget (A)"><input className={formStyles.input} type="number" min={6} value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="80" /></VehicleField>
          <VehicleField label="Breaker rating (A)"><input className={formStyles.input} type="number" value={breaker} onChange={(e) => setBreaker(e.target.value)} placeholder="100" /></VehicleField>
          <VehicleField label="Breaker trip class">
            <select className={formStyles.select} value={tripClass} onChange={(e) => setTripClass(e.target.value as BreakerTripClass)}>
              {TRIP_CLASS_OPTIONS.map((c) => <option key={c} value={c}>Class {c}</option>)}
            </select>
          </VehicleField>
          <VehicleField label="Loop management">
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} label="Active: loop manages it" />
          </VehicleField>
        </div>

        <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--color-tx)' }}>Trip class</strong> sets how much momentary overcurrent the
          breaker tolerates before it trips. Most EV circuits are B or C. Match the letter printed on your
          physical breaker, e.g. C32.
        </p>

        {derateWarn && (
          <div style={{ marginTop: 12, padding: '8px 10px', color: 'var(--evi-warning, #f59e0b)', background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 30%, transparent)', borderRadius: 6, fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{derateWarn}</span>
          </div>
        )}
        {addon && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)', borderRadius: 8, fontSize: 12, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-tx)', marginBottom: 4 }}>Dynamic load management add-on</div>
            {addon.can_enable ? (
              <>
                <p style={{ margin: 0, color: 'var(--color-tx-m)' }}>
                  Circuits need the DLM add-on ({perCharger}). Only chargers assigned
                  to a circuit count — this fleet has {addon.managed_charger_count} managed
                  charger{addon.managed_charger_count === 1 ? '' : 's'} today.
                </p>
                <button type="button" disabled={enableM.isPending || mut.isPending}
                  onClick={() => { setErr(null); enableM.mutate(); }}
                  style={{ marginTop: 10, padding: '7px 12px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {enableM.isPending ? 'Adding…' : 'Add the DLM add-on'}
                </button>
              </>
            ) : (
              // No enable button: the server said this caller cannot add the
              // add-on from here (fleet_admin, or no live subscription). The
              // hint is the server's plain-English next step; the Billing
              // jump appears only when the 402 carries a subscribe_url (a
              // tenant_admin on an unsubscribed fleet).
              <>
                <p style={{ margin: 0, color: 'var(--color-tx-m)' }}>{addon.hint}</p>
                {addon.subscribe_url && (
                  <button type="button" onClick={onGoToBilling}
                    style={{ marginTop: 10, padding: '7px 12px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Open the Billing tab
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {err && (
          <div style={{ marginTop: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={mut.isPending || enableM.isPending}
            style={{ padding: '8px 14px', background: 'transparent', color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          {/* Submit is parked while the add-on panel is up: a re-click would
              hit the same 402. The panel's own action (enable-then-retry, or
              the Billing jump) is the only way forward. */}
          <button type="button" onClick={submit} disabled={mut.isPending || enableM.isPending || addon !== null}
            style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: addon !== null ? 'not-allowed' : 'pointer', opacity: addon !== null ? 0.5 : 1 }}>
            {mut.isPending ? 'Saving…' : (editing ? 'Save changes' : 'Add circuit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CircuitChargersModal({
  fleetId, circuit, chargers, onClose, onChanged,
}: {
  fleetId: string;
  circuit: FleetCircuitRow;
  chargers: FleetChargerRow[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [pick, setPick] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const members = chargers.filter((c) => c.circuit_id === circuit._id);
  const assignable = chargers.filter((c) => c.circuit_id !== circuit._id);
  const capSum = members.reduce((acc, c) => acc + (c.dlm_last_cap_a ?? 0), 0);
  const label = (c: FleetChargerRow): string => c.display_name ? `${c.display_name} (${c.identity})` : c.identity;

  const assign = useMutation({
    mutationFn: (chargerId: string) => assignChargerToCircuit(fleetId, circuit._id, chargerId),
    onSuccess: () => { setPick(''); setErr(null); onChanged(); },
    onError: (e: Error) => setErr(e.message || 'Failed to assign charger'),
  });
  const unassign = useMutation({
    mutationFn: (chargerId: string) => unassignChargerFromCircuit(fleetId, circuit._id, chargerId),
    onSuccess: () => onChanged(),
    onError: (e: Error) => setErr(e.message || 'Failed to remove charger'),
  });

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 94vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--color-sf, #1a1a1a)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>{circuit.name} — chargers</h2>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-tx-m)' }}>
          Loop load{' '}
          <code style={{ color: capSum > circuit.limit_a ? 'var(--evi-error, #ef4444)' : 'var(--color-tx)' }}>{capSum} / {circuit.limit_a} A</code>
          {circuit.is_active ? '' : ' · loop paused (is_active off)'}
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <select className={formStyles.select} value={pick} onChange={(e) => setPick(e.target.value)} style={{ flex: 1 }}>
            <option value="">— Select a charger to assign —</option>
            {assignable.map((c) => (
              <option key={c._id} value={c._id}>{label(c)}{c.circuit_id ? ' · move' : ''}</option>
            ))}
          </select>
          <button type="button" disabled={!pick || assign.isPending}
            onClick={() => { setErr(null); if (pick) assign.mutate(pick); }}
            style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: pick ? 'pointer' : 'default', opacity: pick ? 1 : 0.5 }}>
            Assign
          </button>
        </div>

        {err && (
          <div style={{ marginBottom: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>{err}</div>
        )}

        {members.length === 0 ? (
          <p style={{ color: 'var(--color-tx-m)', fontSize: 13 }}>No chargers on this circuit yet.</p>
        ) : (
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 72px 90px 44px', minWidth: 410, gap: 10, fontSize: 11, color: 'var(--color-tx-m)', padding: '0 4px 4px' }}>
              <span>Charger</span><span>Last cap</span><span>Pushed</span><span />
            </div>
            {members.map((c) => (
              <div key={c._id} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 72px 90px 44px', minWidth: 410, gap: 10, alignItems: 'center', padding: '8px 4px', borderTop: '1px solid var(--color-brd, var(--evi-border))', fontSize: 13 }}>
                <span>{label(c)}</span>
                <code>{fmtCap(c.dlm_last_cap_a)}</code>
                <code style={{ color: 'var(--color-tx-m)' }}>{timeAgo(c.dlm_last_pushed_at)}</code>
                <button type="button" aria-label="Remove from circuit" disabled={unassign.isPending}
                  onClick={() => unassign.mutate(c._id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-error, #ef4444)' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 14px', background: 'transparent', color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Keyfobs tab ────────────────────────────────────────────

// Billing (Warren, 2026-09-16): every keyfob a fleet issues is billed to
// the FLEET — $15 by default, one aggregate charge per bulk upload. The
// two helpers below are shared by the issue and upload modals so the
// price line and the billing prompts read the same in both.

/** "Charged $15.00 to the fleet" / "No charge" for a completed issue.
 *  A prepaid keyfob (an earlier attempt paid, then died before the
 *  insert) is issued against that money: no new charge, and the line
 *  says so rather than "Charged $0.00". */
function keyfobChargeLine(charge: FleetKeyfobCharge & { prepaid_count?: number }, count = 1): string {
  const prepaid = charge.prepaid ? count : (charge.prepaid_count ?? 0);
  const prepaidNote = prepaid > 0
    ? ` ${prepaid === count && count === 1 ? 'Covered' : `${prepaid} covered`} by an earlier charge.`
    : '';
  if (!charge.payment_intent_id || charge.amount_cents <= 0) {
    const base = count === 1 ? 'No charge.' : 'No charge for this upload.';
    return prepaid > 0 ? prepaidNote.trim() : base;
  }
  const chargedFor = count - prepaid;
  return count === 1
    ? `Charged ${formatCents(charge.amount_cents)} to the fleet.`
    : `${chargedFor} keyfob${chargedFor === 1 ? '' : 's'} · ${formatCents(charge.amount_cents)} charged to the fleet.${prepaidNote}`;
}

/** The price line shown BEFORE the click. `count` > 1 is the bulk
 *  estimate ("N keyfobs · $X"), where N is the file's row count and only
 *  the rows actually issued are billed. */
function KeyfobPriceLine({ price, count }: { price: FleetKeyfobPrice | null; count?: number | null }) {
  if (!price) return null;
  let text: string;
  if (price.exempt) text = 'Issued at no charge (super admin).';
  else if (!price.billed) text = 'This tenant issues fleet keyfobs at no charge.';
  else {
    text = `Each keyfob is billed to the fleet: ${formatCents(price.amount_cents)}`;
    if (count && count > 1) {
      text += ` — up to ${count} keyfobs · ${formatCents(count * price.amount_cents)} (only keyfobs actually issued are billed).`;
    }
  }
  return (
    <p style={{ fontSize: 12, color: 'var(--color-tx)', marginTop: 12, lineHeight: 1.5 }}>
      <CreditCard size={12} style={{ verticalAlign: -2, marginRight: 6 }} />{text}
    </p>
  );
}

/**
 * Inline billing prompt: the 402 FLEET_PAST_DUE / FLEET_SUBSCRIPTION_INACTIVE
 * / FLEET_CARD_REQUIRED the server answers before any Stripe call, or —
 * before the click — the standing block the price route already reports
 * (same code, same words, so the prompt does not change after the click).
 * The words come from the server per bucket: an unpaid invoice is "pay
 * it", a canceled subscription is "reactivate it" — a canceled fleet has
 * nothing to bring current. Every fix lives on the Billing tab, so the
 * prompt links there instead of showing a red error the admin cannot act
 * on from here.
 */
function KeyfobBillingPrompt({ block, price, onGoToBilling }: {
  block: FleetKeyfobBillingBlock | null;
  price: FleetKeyfobPrice | null;
  onGoToBilling: () => void;
}) {
  const shown = block
    ?? (price?.standing_block && !price.exempt ? price.standing_block : null);
  const message = shown?.message || null;
  if (!message) return null;
  const heading = shown?.code === 'FLEET_CARD_REQUIRED'
    ? 'Payment card required'
    : shown?.code === 'FLEET_SUBSCRIPTION_INACTIVE'
      ? 'Subscription inactive'
      : 'Unpaid invoice';
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px',
      background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 8%, transparent)',
      border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)',
      borderRadius: 8, fontSize: 12, lineHeight: 1.5,
    }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-tx)', marginBottom: 4 }}>
        {heading}
      </div>
      <p style={{ margin: 0, color: 'var(--color-tx-m)' }}>{message}</p>
      <button type="button" onClick={onGoToBilling}
        style={{
          marginTop: 10, padding: '7px 12px', background: 'var(--evi-accent, #2ECC71)',
          color: '#000', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>
        Go to Billing
      </button>
    </div>
  );
}

function KeyfobsTab({ fleetId, onGoToBilling }: { fleetId: string; onGoToBilling: () => void }) {
  const qc = useQueryClient();
  const fobsQ = useQuery({
    queryKey: ['fleet-keyfobs', fleetId],
    queryFn: () => fetchFleetKeyfobs(fleetId),
  });
  // Vehicles power the optional vehicle binding (Phase 2b) — the issue
  // picker + the list's Vehicle column.
  const vehiclesQ = useQuery({
    queryKey: ['fleet-vehicles', fleetId],
    queryFn: () => fetchFleetVehicles(fleetId),
  });
  // The fleet keyfob price + standing for the modals' price line and the
  // pre-click past-due prompt. Same route the server bills from.
  const priceQ = useQuery({
    queryKey: ['fleet-keyfob-price', fleetId],
    queryFn: () => fetchFleetKeyfobPrice(fleetId),
  });
  const vehicleLabel = (id: string): string => {
    const v = (vehiclesQ.data ?? []).find((x) => x._id === id);
    return v ? (v.label ? `${v.label} · ${v.make} ${v.model}` : `${v.make} ${v.model}`) : 'Unknown vehicle';
  };
  const [issueOpen, setIssueOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // The last single issue's charge, shown under the header after the
  // modal closes (the modal itself closes on success).
  const [lastIssued, setLastIssued] = useState<{ tag_uid: string; charge: FleetKeyfobCharge } | null>(null);

  const toggleActive = useMutation({
    mutationFn: ({ cardId, active }: { cardId: string; active: boolean }) =>
      patchFleetKeyfob(fleetId, cardId, { is_active: active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-keyfobs', fleetId] }),
  });

  const columns: Column<FleetKeyfobRow>[] = [
    { key: 'tag_uid', header: 'Tag UID', cell: (r) => <code style={{ fontSize: 12 }}>{r.tag_uid}</code> },
    { key: 'label', header: 'Label', cell: (r) => r.label ?? '—' },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => r.user_name ? r.user_name : <em style={{ color: 'var(--color-tx-m)' }}>Unassigned pool</em>,
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      cell: (r) => r.vehicle_id ? vehicleLabel(r.vehicle_id) : <em style={{ color: 'var(--color-tx-m)' }}>—</em>,
    },
    {
      key: 'is_active',
      header: 'Status',
      cell: (r) => (
        <button
          type="button"
          onClick={() => toggleActive.mutate({ cardId: r._id, active: !r.is_active })}
          style={{
            padding: '4px 10px',
            background: r.is_active
              ? 'color-mix(in srgb, var(--evi-success, #10b981) 14%, transparent)'
              : 'color-mix(in srgb, var(--evi-error, #ef4444) 14%, transparent)',
            color: r.is_active ? 'var(--evi-success, #10b981)' : 'var(--evi-error, #ef4444)',
            border: 'none', borderRadius: 999,
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {r.is_active ? 'Active' : 'Inactive'}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Keyfobs ({fobsQ.data?.length ?? 0})</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => setBulkOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'transparent',
              color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 8, fontSize: 12, cursor: 'pointer',
            }}>
            <Upload size={12} /> Bulk upload
          </button>
          <button type="button" onClick={() => setIssueOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
            }}>
            <Plus size={12} /> Issue keyfob
          </button>
        </div>
      </div>

      {lastIssued && (
        <p style={{ fontSize: 12, color: 'var(--color-tx-m)', margin: '0 0 12px' }}>
          <Check size={12} style={{ verticalAlign: -2, marginRight: 6, color: 'var(--evi-success, #10b981)' }} />
          Issued <code style={{ fontSize: 12 }}>{lastIssued.tag_uid}</code> · {keyfobChargeLine(lastIssued.charge)}
        </p>
      )}

      {fobsQ.isLoading && <p>Loading…</p>}
      {(fobsQ.data ?? []).length === 0 && !fobsQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No keyfobs issued yet.</p>
      )}
      {(fobsQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetKeyfobRow>
          rows={fobsQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {issueOpen && (
        <IssueKeyfobModal
          fleetId={fleetId}
          vehicles={vehiclesQ.data ?? []}
          price={priceQ.data ?? null}
          onGoToBilling={() => { setIssueOpen(false); onGoToBilling(); }}
          onClose={() => setIssueOpen(false)}
          onIssued={(issued) => {
            setLastIssued(issued);
            void qc.invalidateQueries({ queryKey: ['fleet-keyfobs', fleetId] });
            setIssueOpen(false);
          }}
        />
      )}
      {bulkOpen && (
        <BulkKeyfobsModal
          fleetId={fleetId}
          price={priceQ.data ?? null}
          onGoToBilling={() => { setBulkOpen(false); onGoToBilling(); }}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['fleet-keyfobs', fleetId] });
          }}
        />
      )}
    </div>
  );
}

// ── Plug & charge tab (vehicle ids, free) ──────────────────────────────
// docs/tickets/ocpp-autocharge-vid.md §10. The fleet's cars announce their
// own id at the plug; this tab lists the ids the fleet's chargers have seen
// and not linked, and links one to a fleet vehicle + driver in two clicks.
// No fee: nothing is manufactured or posted. The row then behaves as a
// fleet fob at the reader (billed to the fleet).
function FleetAutochargeTab({ fleetId }: { fleetId: string }) {
  const qc = useQueryClient();
  const credsQ = useQuery({
    queryKey: ['fleet-autocharge', fleetId],
    queryFn: () => fetchFleetAutochargeCredentials(fleetId),
  });
  const unknownQ = useQuery({
    queryKey: ['fleet-autocharge-unknown', fleetId],
    queryFn: () => fetchFleetUnknownVehicleIds(fleetId),
    refetchInterval: 30_000,
  });
  const vehiclesQ = useQuery({
    queryKey: ['fleet-vehicles', fleetId],
    queryFn: () => fetchFleetVehicles(fleetId),
  });
  const driversQ = useQuery({
    queryKey: ['fleet-drivers', fleetId],
    queryFn: () => fetchFleetDrivers(fleetId),
  });
  const [linkFor, setLinkFor] = useState<FleetUnknownVehicleIdRow | null>(null);
  const [releaseFor, setReleaseFor] = useState<FleetAutochargeCredentialRow | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const release = useMutation({
    mutationFn: (cardId: string) => releaseFleetAutochargeCredential(fleetId, cardId),
    onSuccess: () => {
      setReleaseFor(null);
      void qc.invalidateQueries({ queryKey: ['fleet-autocharge', fleetId] });
      void qc.invalidateQueries({ queryKey: ['fleet-autocharge-unknown', fleetId] });
    },
    onError: async (e: Error) => setErr(await getDisplayErrorMessage(e, 'Could not release the vehicle id.')),
  });

  const credColumns: Column<FleetAutochargeCredentialRow>[] = [
    {
      key: 'vehicle',
      header: 'Vehicle',
      cell: (r) => r.vehicle
        ? (r.vehicle.label ? `${r.vehicle.label} · ${r.vehicle.make} ${r.vehicle.model}` : `${r.vehicle.year} ${r.vehicle.make} ${r.vehicle.model}`)
        : <em style={{ color: 'var(--color-tx-m)' }}>—</em>,
    },
    { key: 'driver', header: 'Driver', cell: (r) => r.driver?.name ?? r.driver?.email ?? <em style={{ color: 'var(--color-tx-m)' }}>—</em> },
    { key: 'tag', header: 'Vehicle id', cell: (r) => <code style={{ fontSize: 12 }}>{r.tag_uid}</code> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <span style={{
          padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
          background: r.is_active
            ? 'color-mix(in srgb, var(--evi-success, #10b981) 14%, transparent)'
            : 'color-mix(in srgb, var(--evi-error, #ef4444) 14%, transparent)',
          color: r.is_active ? 'var(--evi-success, #10b981)' : 'var(--evi-error, #ef4444)',
        }}>
          {r.is_active ? 'Active' : r.suspended_by_rule ? 'Paused by rule' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <button type="button" onClick={() => setReleaseFor(r)}
          style={{
            padding: '4px 10px', background: 'transparent',
            border: '1px solid var(--color-brd, var(--evi-border))',
            color: 'var(--color-tx)', borderRadius: 8, fontSize: 11, cursor: 'pointer',
          }}>
          Release
        </button>
      ),
    },
  ];

  const unknownColumns: Column<FleetUnknownVehicleIdRow>[] = [
    { key: 'tag', header: 'Vehicle id', cell: (r) => <code style={{ fontSize: 12 }}>{r.tag_uid}</code> },
    { key: 'charger', header: 'Seen at', cell: (r) => r.charger_label },
    { key: 'when', header: 'Last seen', cell: (r) => new Date(r.last_seen_at).toLocaleString() },
    {
      key: 'link',
      header: '',
      cell: (r) => (
        <button type="button" onClick={() => setLinkFor(r)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', background: 'var(--evi-accent, #2ECC71)',
            color: '#000', border: 'none', borderRadius: 8, fontSize: 11,
            fontWeight: 600, cursor: 'pointer',
          }}>
          <Link2 size={11} /> Link to vehicle
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Plug &amp; charge ({credsQ.data?.length ?? 0})</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
          A linked car starts a session the moment it is plugged into one of the fleet&rsquo;s chargers —
          no app, no fob — billed to the fleet like a keyfob. Free: nothing is manufactured or posted.
          Cars appear below once they have been plugged into a fleet charger.
        </p>
      </div>

      {err && <div style={{ marginBottom: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>{err}</div>}

      {credsQ.isLoading && <p>Loading…</p>}
      {!credsQ.isLoading && (credsQ.data ?? []).length === 0 && (
        <p style={{ color: 'var(--color-tx-m)' }}>No cars linked yet.</p>
      )}
      {(credsQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetAutochargeCredentialRow> rows={credsQ.data ?? []} columns={credColumns} rowKey={(r) => r._id} />
      )}

      <h3 style={{ margin: '22px 0 6px', fontSize: 14 }}>Seen at your chargers, not linked (last 24 h)</h3>
      {unknownQ.isLoading && <p>Loading…</p>}
      {!unknownQ.isLoading && (unknownQ.data ?? []).length === 0 && (
        <p style={{ color: 'var(--color-tx-m)', fontSize: 12 }}>
          Nothing yet. Plug a fleet car into one of the fleet&rsquo;s DC chargers; its id shows up here within a minute.
        </p>
      )}
      {(unknownQ.data ?? []).length > 0 && (
        <ResponsiveTable<FleetUnknownVehicleIdRow> rows={unknownQ.data ?? []} columns={unknownColumns} rowKey={(r) => r.tag_uid} />
      )}

      {linkFor && (
        <LinkFleetVehicleModal
          fleetId={fleetId}
          sighting={linkFor}
          vehicles={vehiclesQ.data ?? []}
          drivers={driversQ.data ?? []}
          onClose={() => setLinkFor(null)}
          onLinked={() => {
            setLinkFor(null);
            void qc.invalidateQueries({ queryKey: ['fleet-autocharge', fleetId] });
            void qc.invalidateQueries({ queryKey: ['fleet-autocharge-unknown', fleetId] });
          }}
        />
      )}

      {releaseFor && (
        <div role="dialog" aria-modal="true" onClick={() => setReleaseFor(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(440px, 92vw)', background: 'var(--color-sf, #1a1a1a)',
              border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
            <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Release vehicle id</h2>
            <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}>
              <code style={{ fontSize: 12 }}>{releaseFor.tag_uid}</code> stops starting sessions on this fleet.
              Whoever plugs the car in next can link it from their own account. Session history stays intact.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setReleaseFor(null)} disabled={release.isPending}
                style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-brd, var(--evi-border))',
                  color: 'var(--color-tx)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={() => release.mutate(releaseFor._id)} disabled={release.isPending}
                style={{ padding: '8px 14px', background: 'var(--evi-error, #ef4444)', color: '#fff', border: 'none',
                  borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {release.isPending ? 'Releasing…' : 'Release'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LinkFleetVehicleModal({
  fleetId, sighting, vehicles, drivers, onClose, onLinked,
}: {
  fleetId: string;
  sighting: FleetUnknownVehicleIdRow;
  vehicles: FleetVehicleRow[];
  drivers: FleetDriverRow[];
  onClose: () => void;
  onLinked: () => void;
}) {
  const [vehicleId, setVehicleId] = useState('');
  const [userId, setUserId] = useState('');
  const [label, setLabel] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const vehicle = vehicles.find((v) => v._id === vehicleId) ?? null;
  // The vehicle's primary driver is the default; the picker only needs a
  // choice when there is none.
  const effectiveDriver = userId || vehicle?.primary_driver_id || '';
  const mut = useMutation({
    mutationFn: () => issueFleetAutochargeCredential(fleetId, {
      tag_uid: sighting.tag_uid,
      vehicle_id: vehicleId,
      user_id: effectiveDriver || null,
      label: label.trim() || undefined,
    }),
    onSuccess: () => onLinked(),
    onError: async (e: Error) => setErr(await getDisplayErrorMessage(e, 'Could not link the car.')),
  });
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(440px, 92vw)', background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>Link car to a fleet vehicle</h2>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-tx-m)' }}>
          <code style={{ fontSize: 12 }}>{sighting.tag_uid}</code> · seen at {sighting.charger_label}
        </p>
        <label className={formStyles.label}>Vehicle</label>
        <select className={formStyles.select} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
          <option value="">Choose a fleet vehicle…</option>
          {vehicles.map((v) => (
            <option key={v._id} value={v._id}>{v.label ? `${v.label} · ` : ''}{v.year} {v.make} {v.model}</option>
          ))}
        </select>
        <label className={formStyles.label} style={{ marginTop: 10 }}>Driver</label>
        <select className={formStyles.select} value={effectiveDriver} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Choose a driver…</option>
          {drivers.map((d) => (
            <option key={d._id} value={d._id}>{d.name || d.email}</option>
          ))}
        </select>
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
          Sessions bill to the fleet. A driver is required for now — a pool vehicle with no driver cannot
          start at the reader yet.
        </p>
        <label className={formStyles.label} style={{ marginTop: 10 }}>Label (optional)</label>
        <input className={formStyles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Van #1" />
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 10 }}>No fee.</p>
        {err && <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={mut.isPending}
            style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-brd, var(--evi-border))',
              color: 'var(--color-tx)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={() => { setErr(null); mut.mutate(); }}
            disabled={mut.isPending || !vehicleId || !effectiveDriver}
            style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none',
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {mut.isPending ? 'Linking…' : 'Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssueKeyfobModal({
  fleetId, vehicles, price, onGoToBilling, onClose, onIssued,
}: {
  fleetId: string;
  vehicles: FleetVehicleRow[];
  price: FleetKeyfobPrice | null;
  onGoToBilling: () => void;
  onClose: () => void;
  onIssued: (issued: { tag_uid: string; charge: FleetKeyfobCharge }) => void;
}) {
  const [tag, setTag] = useState('');
  const [label, setLabel] = useState('');
  const [userId, setUserId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // 402 FLEET_PAST_DUE / FLEET_CARD_REQUIRED → inline prompt with the
  // Billing link; every other failure (a declined card, a 409 dup) is the
  // server's own message in the error slot.
  const [block, setBlock] = useState<FleetKeyfobBillingBlock | null>(null);
  const mut = useMutation({
    mutationFn: () => issueFleetKeyfob(fleetId, {
      tag_uid: tag.trim(),
      label: label.trim() || undefined,
      user_id: userId.trim() || null,
      vehicle_id: vehicleId || null,
    }),
    onSuccess: (res) => { onIssued({ tag_uid: res.tag_uid, charge: res.charge }); },
    onError: async (e: Error) => {
      const billing = await getFleetKeyfobBillingBlock(e);
      if (billing) { setBlock(billing); return; }
      setErr(await getDisplayErrorMessage(e, 'Could not issue the keyfob.'));
    },
  });
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(440px, 92vw)', background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>Issue keyfob</h2>
        <label className={formStyles.label}>RFID tag UID</label>
        <input className={formStyles.input} value={tag} onChange={(e) => setTag(e.target.value)} placeholder="A1B2C3D4E5F6" />
        <label className={formStyles.label} style={{ marginTop: 10 }}>Label (optional)</label>
        <input className={formStyles.input} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Van #1 dashboard" />
        <label className={formStyles.label} style={{ marginTop: 10 }}>Driver user ID (optional)</label>
        <input className={formStyles.input} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="leave empty for unassigned pool" />
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
          Empty driver ID issues an unassigned-pool keyfob — taps bill the fleet with no driver name.
        </p>
        <label className={formStyles.label} style={{ marginTop: 10 }}>Vehicle (optional)</label>
        <select className={formStyles.select} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
          <option value="">— None —</option>
          {vehicles.map((v) => (
            <option key={v._id} value={v._id}>
              {v.label ? `${v.label} · ${v.make} ${v.model}` : `${v.year} ${v.make} ${v.model}`}
            </option>
          ))}
        </select>
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
          Binding a vehicle attributes this keyfob&rsquo;s sessions to it (Phase 2b).
        </p>
        <KeyfobPriceLine price={price} />
        <KeyfobBillingPrompt block={block} price={price} onGoToBilling={onGoToBilling} />
        {err && (
          <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
            {err}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={mut.isPending}
            style={{
              padding: '8px 14px', background: 'transparent',
              border: '1px solid var(--color-brd, var(--evi-border))',
              color: 'var(--color-tx)', borderRadius: 8, fontSize: 13, cursor: 'pointer',
            }}>
            Cancel
          </button>
          <button type="button" onClick={() => { setErr(null); setBlock(null); mut.mutate(); }}
            disabled={mut.isPending || !tag.trim()}
            style={{
              padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
            {mut.isPending ? 'Issuing…' : 'Issue'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sessions tab ───────────────────────────────────────────

function SessionsTab({ fleetId }: { fleetId: string }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const sessionsQ = useQuery({
    queryKey: ['fleet-sessions', fleetId, from, to, page],
    queryFn: () => fetchFleetSessions(fleetId, {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      page, limit: 50,
    }),
  });
  const data = sessionsQ.data?.data ?? [];
  const meta = sessionsQ.data?.meta;
  // Export CSV: fetched with the signed-in token and saved (a plain link
  // reached the Api without it and got a 401).
  const downloads = useAuthedDownload();

  const aggregates = useMemo(() => {
    const totalSessions = meta?.total ?? data.length;
    const kwhSum = data.reduce((s, r) => s + (r.energy_kwh || 0), 0);
    const costSum = data.reduce((s, r) => s + (r.cost?.total || 0), 0);
    const durSum = data.reduce((s, r) => s + (r.duration_minutes || 0), 0);
    return {
      sessions: totalSessions,
      kwh: kwhSum,
      costCents: costSum,
      avgDuration: data.length > 0 ? Math.round(durSum / data.length) : 0,
    };
  }, [data, meta?.total]);

  const columns: Column<FleetSessionRow>[] = [
    { key: 'started_at', header: 'Started', cell: (r) => new Date(r.started_at).toLocaleString() },
    { key: 'transaction_id', header: 'Txn', cell: (r) => r.transaction_id ?? '—' },
    // Who started it (item 9, 2026-10-23): the name, linked to the driver
    // profile when the Api says it opens for this viewer.
    { key: 'driver', header: 'Driver', cell: (r) => <DriverCell row={r} /> },
    { key: 'energy', header: 'kWh', cell: (r) => r.energy_kwh.toFixed(2) },
    { key: 'duration', header: 'Duration', cell: (r) => `${r.duration_minutes}m` },
    { key: 'cost', header: 'Cost', cell: (r) => formatCents(r.cost?.total ?? 0) },
    { key: 'status', header: 'Status', cell: (r) => r.status },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-stats-columns, repeat(4, 1fr))', gap: 10, marginBottom: 16 }}>
        <Aggregate label="Sessions" value={String(aggregates.sessions)} />
        <Aggregate label="Total kWh" value={aggregates.kwh.toFixed(1)} />
        <Aggregate label="Total cost" value={formatCents(aggregates.costCents)} />
        <Aggregate label="Avg duration" value={`${aggregates.avgDuration}m`} />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <label className={formStyles.label}>From</label>
          <input type="date" className={formStyles.input} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className={formStyles.label}>To</label>
          <input type="date" className={formStyles.input} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button
          type="button"
          disabled={downloads.isBusy('csv')}
          aria-busy={downloads.isBusy('csv')}
          onClick={() => void downloads.download(
            'csv',
            fleetSessionsCsvPath(fleetId, { ...(from ? { from } : {}), ...(to ? { to } : {}) }),
            `invoices-fleet-${fleetId}.csv`,
          )}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', background: 'transparent',
            color: 'var(--color-tx)',
            border: '1px solid var(--color-brd, var(--evi-border))',
            borderRadius: 8, fontSize: 12, textDecoration: 'none',
            cursor: downloads.isBusy('csv') ? 'progress' : 'pointer',
            opacity: downloads.isBusy('csv') ? 0.7 : 1,
          }}
        >
          {downloads.isBusy('csv') ? <Loader2 size={12} className="spin" /> : <Download size={12} />} Export CSV
        </button>
      </div>
      {downloads.error && (
        <div role="alert" style={{ marginBottom: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>
          {downloads.error}
        </div>
      )}

      {sessionsQ.isLoading && <p>Loading…</p>}
      {data.length === 0 && !sessionsQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No sessions in this range.</p>
      )}
      {data.length > 0 && (
        <ResponsiveTable<FleetSessionRow>
          rows={data}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {meta && meta.total > meta.limit && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span style={{ fontSize: 12, alignSelf: 'center' }}>
            Page {meta.page} of {Math.ceil(meta.total / meta.limit)}
          </span>
          <button
            disabled={meta.page * meta.limit >= meta.total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function Aggregate({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      padding: '12px 14px',
      background: 'var(--color-sf2, #1a1a1a)',
      border: '1px solid var(--color-brd, var(--evi-border))',
      borderRadius: 10,
    }}>
      <p style={{ margin: 0, fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--color-tx-m)' }}>
        {label}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>{value}</p>
    </div>
  );
}

// ── Billing tab (Phase 4 Item 7) ──────────────────────────────────
//
// Replaces the Phase 3 placeholder with subscription management:
//   • Plan card (cadence + line items + switch action)
//   • Status card (status badge + next billing / trial end)
//   • Payment method card (last 4 + update CTA)
//   • Billing history (last 12 invoices with download links)
//   • Cancel / Reactivate at the bottom
//
// Cancel uses cancel_at_period_end so service stays up until the
// current period ends; reactivate is only meaningful in that
// window. Update-PM mints a SetupIntent (creating the fleet's Stripe
// customer first when a migrated fleet has none), CardSetupModal
// collects the card through Stripe Elements, and on confirmation the
// tab posts the pm id back to the same route's CONFIRM phase so the
// Fleet-doc mirror — what session start and "No card on file" read —
// flips at once. (Not PATCH /admin/fleets/:id: it attaches
// unconditionally, and Stripe rejects re-attaching a card Elements
// already attached.) The keyfob paywall's FLEET_CARD_REQUIRED lands
// here, so this has to complete end to end without the Stripe
// Dashboard.
//
// The Plan card multiplies billed_quantity (the sync's max(live,
// floor)), never the live roster: a website-subscribed fleet carries
// a seat floor above its roster until the chargers are assigned.

//
// No price lives here (Warren, 21 October 2026, night: "Read both prices
// from the live Stripe Price objects, never calculate one from the
// other"). The summary carries the fleet base and the per-charger price
// for each cadence, each from its own Stripe Price, null where Stripe
// has none.
const NO_PRICES: PlanPricePair = { annual: null, monthly: null };

function fmtDate(value: string | number | null | undefined): string {
  if (!value) return '—';
  const ts = typeof value === 'number' ? value * 1000 : new Date(value).getTime();
  if (!Number.isFinite(ts)) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function statusColor(status: string | null): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'var(--evi-accent, #2ECC71)';
    case 'past_due':
    case 'unpaid':
      return 'var(--evi-warning, #f59e0b)';
    case 'canceled':
    case 'incomplete':
      return 'var(--evi-error, #ef4444)';
    default:
      return 'var(--color-tx-m)';
  }
}

function BillingTab({ fleetId, fleet }: { fleetId: string; fleet: FleetSummary }) {
  const qc = useQueryClient();
  const billingQ = useQuery({
    queryKey: ['fleet-billing', fleetId],
    queryFn: () => fetchFleetBilling(fleetId),
  });
  // Solely to name the consequence in the cancel confirmation. The
  // billing payload counts CHARGERS (that is what Stripe bills); the
  // people who stop being paid for are the drivers, so the number in
  // the warning has to come from the roster. Same query key as the
  // Drivers tab, so opening both costs one fetch.
  const driversQ = useQuery({
    queryKey: ['fleet-drivers', fleetId],
    queryFn: () => fetchFleetDrivers(fleetId),
    staleTime: 60_000,
  });

  const [switchCadenceOpen, setSwitchCadenceOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cardSetup, setCardSetup] = useState<FleetPaymentMethodSetup | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const switchM = useMutation({
    mutationFn: (cadence: 'monthly' | 'annual') => switchFleetCadence(fleetId, cadence),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet-billing', fleetId] });
      setSwitchCadenceOpen(false);
      setActionError(null);
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const cancelM = useMutation({
    mutationFn: () => cancelFleetSubscription(fleetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet-billing', fleetId] });
      setCancelOpen(false);
      setActionError(null);
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const reactivateM = useMutation({
    mutationFn: () => reactivateFleetSubscription(fleetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet-billing', fleetId] });
      setActionError(null);
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const updatePmM = useMutation({
    mutationFn: () => startFleetPaymentMethodUpdate(fleetId),
    onSuccess: (data) => {
      setCardSetup(data);
      setActionError(null);
    },
    onError: async (e: Error) => setActionError(
      await getDisplayErrorMessage(e, e.message || 'Could not start the card update.'),
    ),
  });

  // Runs once Stripe has attached the card (CardSetupModal). The CONFIRM
  // phase sets the Stripe-side invoice default AND writes the Fleet-doc
  // mirror; there is no webhook backstop for fleet customers, so a
  // failure here is surfaced in the modal with a retry of this step
  // alone. On success both caches refetch so "No card on file" flips
  // and the summary's PM id updates.
  const onCardConfirmed = async (paymentMethodId: string) => {
    try {
      await confirmFleetPaymentMethod(fleetId, paymentMethodId);
    } catch (e) {
      throw new Error(await getDisplayErrorMessage(
        e,
        'The card was saved with Stripe but could not be set as the fleet default. Retry.',
      ));
    }
    setCardSetup(null);
    setActionError(null);
    void qc.invalidateQueries({ queryKey: ['fleet-billing', fleetId] });
    void qc.invalidateQueries({ queryKey: ['fleet', fleetId] });
  };

  if (billingQ.isLoading) {
    return (
      <div style={{ padding: 16, color: 'var(--color-tx-m)' }}>
        <Loader2 size={14} className="spin" /> Loading billing…
      </div>
    );
  }
  if (!billingQ.data) {
    return <p style={{ color: 'var(--color-tx-m)' }}>No billing data.</p>;
  }
  const b = billingQ.data;
  // What Stripe bills: max(assigned chargers, checkout seat floor).
  const billedQuantity = b.billed_quantity;
  const floorAboveLive = (b.subscribed_charger_floor ?? 0) > b.charger_count;
  const isAnnual = b.billing_cadence === 'annual';
  const basePrices = b.plan_prices?.base ?? NO_PRICES, perChargerPrices = b.plan_prices?.per_charger ?? NO_PRICES;
  const base = basePrices[b.billing_cadence], perCharger = perChargerPrices[b.billing_cadence];
  const totalCents = planCost([{ price: base, quantity: 1 }, { price: perCharger, quantity: billedQuantity }]);
  const cadenceLabel = isAnnual ? '/year' : '/month';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {actionError && (
        <div style={{
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--evi-error, #ef4444) 12%, transparent)',
          border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 35%, transparent)',
          borderRadius: 8,
          color: 'var(--evi-error, #ef4444)',
          fontSize: 13,
        }}>
          {actionError}
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12,
      }}>
        {/* Plan card */}
        <section style={{
          padding: '16px 18px', background: 'var(--color-sf2, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 10,
        }}>
          <p style={{
            margin: 0, fontSize: 10, textTransform: 'uppercase',
            color: 'var(--color-tx-m)', letterSpacing: '1.2px',
          }}>
            Plan · {isAnnual ? 'Annual' : 'Monthly'}
          </p>
          {totalCents !== null ? (
            <p style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {formatCents(totalCents)}<span style={{ fontSize: 14, color: 'var(--color-tx-m)', fontWeight: 400 }}>{cadenceLabel}</span>
            </p>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 600 }}>{PLAN_PRICE_UNCONFIRMED}.</p>
          )}
          <ul style={{
            margin: '10px 0 12px', padding: 0, listStyle: 'none',
            fontSize: 12, color: 'var(--color-tx-m)',
          }}>
            <li style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span>Base fleet fee</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{base ? <>{formatCents(base.unit_price_cents, base.currency)}{cadenceLabel}</> : PLAN_PRICE_UNCONFIRMED}</span>
            </li>
            <li style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span>{billedQuantity} charger{billedQuantity === 1 ? '' : 's'}{perCharger ? <> × {formatCents(perCharger.unit_price_cents, perCharger.currency)}{cadenceLabel}</> : ''}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{perCharger ? <>{formatCents(perCharger.unit_price_cents * billedQuantity, perCharger.currency)}{cadenceLabel}</> : PLAN_PRICE_UNCONFIRMED}</span>
            </li>
            {floorAboveLive && (
              <li style={{ padding: '2px 0 0', fontSize: 11 }}>
                {b.subscribed_charger_floor} seats reserved · {b.charger_count} registered
              </li>
            )}
          </ul>
          <button
            type="button"
            onClick={() => setSwitchCadenceOpen(true)}
            disabled={!b.stripe_subscription_id || b.cancel_at_period_end}
            style={{
              padding: '8px 14px', fontSize: 13,
              background: 'var(--evi-accent, #2ECC71)', color: '#000',
              border: 'none', borderRadius: 8, fontWeight: 600,
              cursor: b.stripe_subscription_id && !b.cancel_at_period_end ? 'pointer' : 'not-allowed',
              opacity: b.stripe_subscription_id && !b.cancel_at_period_end ? 1 : 0.5,
            }}
          >
            Switch to {isAnnual ? 'monthly' : 'annual'}
          </button>
        </section>

        {/* Status card */}
        <section style={{
          padding: '16px 18px', background: 'var(--color-sf2, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 10,
        }}>
          <p style={{
            margin: 0, fontSize: 10, textTransform: 'uppercase',
            color: 'var(--color-tx-m)', letterSpacing: '1.2px',
          }}>
            Status
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 16, fontWeight: 700, color: statusColor(b.subscription_status) }}>
            {b.subscription_status ?? 'No subscription'}
            {b.cancel_at_period_end ? ' · canceling' : ''}
          </p>
          {b.subscription_status === 'trialing' && b.trial_ends_at && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
              Trial ends {fmtDate(b.trial_ends_at)}
            </p>
          )}
          {b.current_period_end && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
              {b.cancel_at_period_end ? 'Ends' : 'Next billing'}: {fmtDate(b.current_period_end)}
            </p>
          )}
          {b.upcoming_invoice && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
              Next charge estimate: <strong>{formatCents(b.upcoming_invoice.amount_due_cents)}</strong>
            </p>
          )}
        </section>

        {/* Payment method card */}
        <section style={{
          padding: '16px 18px', background: 'var(--color-sf2, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 10,
        }}>
          <p style={{
            margin: 0, fontSize: 10, textTransform: 'uppercase',
            color: 'var(--color-tx-m)', letterSpacing: '1.2px',
          }}>
            Payment method
          </p>
          {b.default_card ? (
            <p style={{ margin: '8px 0 0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <CreditCard size={14} /> {b.default_card.brand?.toUpperCase()} ···· {b.default_card.last4}
              <span style={{ color: 'var(--color-tx-m)', fontSize: 12, marginLeft: 4 }}>
                exp {b.default_card.exp_month}/{String(b.default_card.exp_year).slice(-2)}
              </span>
            </p>
          ) : (
            <p style={{ margin: '8px 0 0', color: 'var(--color-tx-m)', fontSize: 13 }}>No card on file</p>
          )}
          <button
            type="button"
            onClick={() => updatePmM.mutate()}
            disabled={updatePmM.isPending}
            style={{
              marginTop: 12,
              padding: '8px 14px', fontSize: 13,
              background: 'transparent', color: 'var(--evi-accent, #2ECC71)',
              border: '1px solid var(--evi-accent, #2ECC71)', borderRadius: 8,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            {updatePmM.isPending ? 'Starting…' : b.default_card ? 'Update payment method' : 'Add card'}
          </button>
        </section>
      </div>

      {/* Invoice history */}
      <section>
        <h3 style={{ margin: '8px 0', fontSize: 14 }}>Billing history</h3>
        {b.recent_invoices.length === 0 ? (
          <p style={{ color: 'var(--color-tx-m)' }}>No invoices yet.</p>
        ) : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Number</th>
                <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                <th style={{ textAlign: 'right', padding: 8 }}>Amount</th>
                <th style={{ textAlign: 'right', padding: 8 }}></th>
              </tr>
            </thead>
            <tbody>
              {b.recent_invoices.map((inv) => (
                <tr key={inv.id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                  <td style={{ padding: 8 }}>{fmtDate(inv.created ?? inv.period_start)}</td>
                  <td style={{ padding: 8 }}>{inv.number ?? inv.id.slice(-8)}</td>
                  <td style={{ padding: 8, color: statusColor(inv.status) }}>{inv.status ?? '—'}</td>
                  <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCents(inv.amount_paid_cents || inv.amount_due_cents)}
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
                        View
                      </a>
                    )}
                    {inv.invoice_pdf && (
                      <a href={inv.invoice_pdf} target="_blank" rel="noreferrer">
                        <Download size={12} style={{ verticalAlign: 'middle' }} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Cancel / reactivate */}
      <section style={{ marginTop: 12, padding: '14px 18px',
        border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 10 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Subscription</p>
        {b.cancel_at_period_end ? (
          <>
            <p style={{ margin: '8px 0', fontSize: 13, color: 'var(--color-tx-m)' }}>
              Scheduled to cancel on <strong>{fmtDate(b.cancel_at ? b.cancel_at * 1000 : null)}</strong>.
              You can reactivate before that date — drivers will continue charging without interruption.
            </p>
            <button
              type="button"
              onClick={() => reactivateM.mutate()}
              disabled={reactivateM.isPending}
              style={{
                padding: '8px 14px', fontSize: 13,
                background: 'var(--evi-accent, #2ECC71)', color: '#000',
                border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {reactivateM.isPending ? 'Reactivating…' : 'Reactivate subscription'}
            </button>
          </>
        ) : (
          <>
            <p style={{ margin: '8px 0', fontSize: 13, color: 'var(--color-tx-m)' }}>
              Cancel at the end of the current billing period. Drivers stay on fleet billing
              until {fmtDate(b.current_period_end)} and then fall back to personal cards.
            </p>
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              disabled={!b.stripe_subscription_id}
              style={{
                padding: '8px 14px', fontSize: 13,
                background: 'transparent', color: 'var(--evi-error, #ef4444)',
                border: '1px solid var(--evi-error, #ef4444)',
                borderRadius: 8, fontWeight: 600,
                cursor: b.stripe_subscription_id ? 'pointer' : 'not-allowed',
              }}
            >
              Cancel subscription
            </button>
          </>
        )}
      </section>

      <p style={{ marginTop: 4, fontSize: 11, color: 'var(--color-tx-m)' }}>
        Stripe customer: <code>{b.stripe_customer_id || fleet.stripe_customer_id || 'none yet — created when a card is added'}</code>
      </p>

      {/* Modals */}
      {switchCadenceOpen && (
        <SwitchCadenceModal
          fromCadence={b.billing_cadence}
          chargerCount={billedQuantity}
          basePrices={basePrices}
          perChargerPrices={perChargerPrices}
          onClose={() => setSwitchCadenceOpen(false)}
          onConfirm={() => switchM.mutate(isAnnual ? 'monthly' : 'annual')}
          submitting={switchM.isPending}
        />
      )}
      {cancelOpen && (
        <ConfirmCancelModal
          periodEnd={b.current_period_end}
          driverCount={driversQ.data?.length ?? null}
          onClose={() => setCancelOpen(false)}
          onConfirm={() => cancelM.mutate()}
          submitting={cancelM.isPending}
        />
      )}
      {cardSetup && (
        <CardSetupModal
          title={b.default_card ? 'Update payment method' : 'Add a card'}
          clientSecret={cardSetup.client_secret}
          stripeCustomerId={cardSetup.stripe_customer_id}
          onConfirmed={onCardConfirmed}
          onClose={() => setCardSetup(null)}
        />
      )}
    </div>
  );
}

/** Each cadence's cost from its own Stripe Prices (the base and per charger), never one worked out from the other and
 *  no "you save" derived from them (Warren, 21 October 2026, night). */
function SwitchCadenceModal({
  fromCadence, chargerCount, basePrices, perChargerPrices, onClose, onConfirm, submitting,
}: {
  fromCadence: 'monthly' | 'annual';
  chargerCount: number;
  basePrices: PlanPricePair;
  perChargerPrices: PlanPricePair;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const toAnnual = fromCadence === 'monthly';
  const cost = (c: 'monthly' | 'annual') => planCost([{ price: basePrices[c], quantity: 1 }, { price: perChargerPrices[c], quantity: chargerCount }]);
  const monthlyTotal = cost('monthly'), annualTotal = cost('annual');

  return (
    <ModalShell title={toAnnual ? 'Switch to annual billing' : 'Switch to monthly billing'} onClose={onClose}>
      <p style={{ fontSize: 13, color: 'var(--color-tx-m)' }}>
        {toAnnual
          ? 'Switch to annual billing, paid for the year ahead. The change takes effect on your next billing cycle. Stripe will charge the prorated difference on your next invoice.'
          : 'Switch back to monthly. The change takes effect on your next billing cycle. Stripe will refund the unused annual portion as a prorated credit.'}
      </p>
      <div style={{
        margin: '12px 0',
        padding: 14,
        background: 'var(--color-sf2, #1a1a1a)',
        border: '1px solid var(--color-brd, var(--evi-border))',
        borderRadius: 8,
        fontSize: 13,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span>Monthly cost</span>
          <strong>{monthlyTotal !== null ? `${formatCents(monthlyTotal)}/month` : PLAN_PRICE_UNCONFIRMED}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span>Annual cost</span>
          <strong>{annualTotal !== null ? `${formatCents(annualTotal)}/year` : PLAN_PRICE_UNCONFIRMED}</strong>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose}
          style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, cursor: 'pointer', color: 'var(--color-tx)' }}>
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={submitting}
          style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          {submitting ? 'Switching…' : `Switch to ${toAnnual ? 'annual' : 'monthly'}`}
        </button>
      </div>
    </ModalShell>
  );
}

/**
 * Cancelling is the fleet admin's to do — it is their contract — so
 * this modal does not gate it. What it must do is NAME THE CONSEQUENCE,
 * because the consequence is not local to billing: at period end the
 * `customer.subscription.deleted` webhook clears `fleet.is_active`
 * (webhooks/stripe.ts), and StartTransaction refuses fleet billing for
 * an inactive fleet, so every driver silently starts paying with their
 * own card. The old copy said "drivers will fall back to personal
 * billing" without saying how many people that is.
 */
function ConfirmCancelModal({
  periodEnd, driverCount, onClose, onConfirm, submitting,
}: {
  periodEnd: string | null;
  /** null while the roster is still loading — the warning then stays
   *  truthful by naming the effect without a number rather than
   *  claiming "0 drivers". */
  driverCount: number | null;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const who = driverCount === null
    ? 'every driver in this fleet'
    : driverCount === 1
      ? 'your 1 driver'
      : `all ${driverCount} drivers`;
  return (
    <ModalShell title="Cancel subscription?" onClose={onClose}>
      <p style={{ fontSize: 13, color: 'var(--color-tx)' }}>
        Cancelling ends fleet-paid charging for <strong>{who}</strong>, who will
        pay with their own cards until you reactivate.
      </p>
      <p style={{ fontSize: 13, color: 'var(--color-tx-m)' }}>
        Your fleet stays active until <strong>{fmtDate(periodEnd)}</strong>. The
        change takes effect after that date.
      </p>
      <p style={{ fontSize: 13, color: 'var(--color-tx-m)' }}>
        You can reactivate any time before that date without losing data.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose}
          style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, cursor: 'pointer', color: 'var(--color-tx)' }}>
          Keep subscription
        </button>
        <button type="button" onClick={onConfirm} disabled={submitting}
          style={{ padding: '8px 14px', background: 'var(--evi-error, #ef4444)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          {submitting ? 'Canceling…' : 'Cancel subscription'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Admins tab ─────────────────────────────────────────────

function AdminsTab({ fleetId, fleet }: { fleetId: string; fleet: FleetSummary }) {
  // POST /admin/fleets/:id/admins 403s for fleet_admin callers ("cannot
  // add other fleet admins"), and Team is not in their nav — so the
  // affordance is hidden for them and the hint says who can.
  //
  // This tab is ALSO what the Fleet menu's "Settings" leaf lands on
  // (App.tsx maps /fleet/settings -> initialTab 'admins'), so for a
  // fleet admin it is the settings page in every sense that matters to
  // them. That is why the operator-owned terms are rendered here: the
  // ruling asks the Settings page to state them read-only rather than
  // leave the persona guessing which knobs exist.
  const isFleetAdmin = useAuthStore((s) => s.user?.role) === 'fleet_admin';
  const qc = useQueryClient();
  const adminsQ = useQuery({
    queryKey: ['fleet-admins', fleetId],
    queryFn: () => fetchFleetAdmins(fleetId),
  });
  const [addOpen, setAddOpen] = useState(false);
  // Single-select via the shared typeahead (array shape is the picker's
  // contract; maxSelections=1 keeps it to one). Replaces the raw-ObjectId
  // entry box — an admin knows the person's email, not their id.
  const [newIds, setNewIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (uid: string) => addFleetAdmin(fleetId, uid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fleet-admins', fleetId] });
      setAddOpen(false);
      setNewIds([]);
      setErr(null);
    },
    onError: (e: Error) => setErr(e.message),
  });
  const remove = useMutation({
    mutationFn: (uid: string) => removeFleetAdmin(fleetId, uid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fleet-admins', fleetId] }),
  });

  const columns: Column<FleetAdminRow>[] = [
    { key: 'name', header: 'Name', cell: (r) => r.name },
    { key: 'email', header: 'Email', cell: (r) => r.email },
    { key: 'is_active', header: 'Status', cell: (r) => r.is_active ? 'Active' : 'Inactive' },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <button type="button" aria-label="Remove admin"
          onClick={() => { if (confirm(`Remove ${r.name} as fleet admin?`)) remove.mutate(r._id); }}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-error, #ef4444)' }}>
          <Trash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <div>
      {isFleetAdmin && <FleetOperatorOnlySettings fleet={fleet} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Fleet admins ({adminsQ.data?.length ?? 0})</h3>
        {isFleetAdmin ? (
          <span style={{ fontSize: 11, color: 'var(--color-tx-m)' }}>
            Ask your operator to add another fleet admin.
          </span>
        ) : (
          <button type="button" onClick={() => setAddOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
              fontWeight: 600, cursor: 'pointer',
            }}>
            <Plus size={12} /> Add admin
          </button>
        )}
      </div>
      {(adminsQ.data ?? []).length === 0 ? (
        <p style={{ color: 'var(--color-tx-m)' }}>No fleet admins yet.</p>
      ) : (
        <ResponsiveTable<FleetAdminRow>
          rows={adminsQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}
      {addOpen && (
        <ModalShell
          title="Add fleet admin"
          onClose={() => { setAddOpen(false); setNewIds([]); setErr(null); }}
        >
          <label className={formStyles.label}>Driver account</label>
          {/* GET /admin/users/search defaults to role=driver, which is
              exactly the population this route accepts (it 409s on native
              teammates), so the picker cannot offer an invalid target. */}
          <UserTypeahead
            selectedUserIds={newIds}
            onChange={setNewIds}
            maxSelections={1}
            placeholder="Search by email or name…"
            disabled={add.isPending}
            ariaLabel="Search driver accounts"
          />
          <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
            Fleet admin is granted to a driver account. Someone with no account
            yet is invited from Team — they are added to this fleet as part of
            the invite.
          </p>
          {err && (
            <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
              {err}
            </div>
          )}
          <ModalFooter
            onCancel={() => { setAddOpen(false); setNewIds([]); setErr(null); }}
            submitting={add.isPending}
            onSubmit={() => {
              const uid = newIds[0];
              if (!uid) return;
              setErr(null);
              add.mutate(uid);
            }}
            submitLabel="Add admin"
            submitDisabled={newIds.length === 0}
          />
        </ModalShell>
      )}
    </div>
  );
}

// ── Invite-by-email modal (Phase 3a Item 7) ──────────────────

function InviteByEmailModal({
  fleetId, onClose, onDone,
}: {
  fleetId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // The join link goes to the mailbox only (Warren, 2026-09-26 review): the Api never returns it, so nothing here shows
  // or copies one (29 October 2026).
  const [result, setResult] = useState<{ email: string } | null>(null);
  const mut = useMutation({
    mutationFn: () => inviteFleetDriverByEmail(fleetId, email.trim()),
    onSuccess: () => {
      setResult({ email: email.trim() });
      setErr(null);
      onDone();
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <ModalShell title="Add driver by email" onClose={onClose}>
      {!result && (
        <>
          <label className={formStyles.label}>Driver email</label>
          <input
            className={formStyles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="driver@example.com"
          />
          <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
            An invite link is emailed to this address.
            A driver who already has an account joins once they open it and sign
            in with the sign-in link we email them.
          </p>
          {err && (
            <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
              {err}
            </div>
          )}
          <ModalFooter
            onCancel={onClose}
            submitting={mut.isPending}
            onSubmit={() => { setErr(null); mut.mutate(); }}
            submitLabel="Send invite"
            submitDisabled={!email.includes('@')}
          />
        </>
      )}
      {result && (
        <>
          <p style={{ fontSize: 13, marginBottom: 10 }}>
            ✅ Invite emailed to {result.email}. If they already have an account,
            they join once they open the link and sign in with the emailed sign-in link.
          </p>
          <ModalFooter
            onCancel={onClose}
            submitting={false}
            onSubmit={onClose}
            submitLabel="Done"
            submitDisabled={false}
          />
        </>
      )}
    </ModalShell>
  );
}

// ── Bulk drivers CSV upload (Phase 3a Item 1) ────────────────

function BulkDriversModal({
  fleetId, onClose, onDone,
}: {
  fleetId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<BulkDriverUploadSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => bulkUploadDrivers(fleetId, file!),
    onSuccess: (data) => {
      setSummary(data);
      setErr(null);
      onDone();
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <ModalShell title="Bulk upload drivers" onClose={onClose}>
      {!summary && (
        <>
          <p style={{ fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
            CSV columns: <code>email</code> (required), <code>name</code> (optional), <code>send_invite</code> (optional, default true).
            Max 2 MB. Every address gets an emailed join link. A driver who already has an
            account joins once they open it and sign in with the sign-in link we email them.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={{ marginTop: 10 }}
          />
          {err && (
            <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
              {err}
            </div>
          )}
          <ModalFooter
            onCancel={onClose}
            submitting={mut.isPending}
            onSubmit={() => { setErr(null); if (file) mut.mutate(); }}
            submitLabel="Upload"
            submitDisabled={!file}
          />
        </>
      )}
      {summary && (
        <>
          <p style={{ fontSize: 13, fontWeight: 600 }}>
            Processed {summary.total_rows} row(s).
          </p>
          <ul style={{ fontSize: 12, listStyle: 'none', padding: 0, margin: '8px 0' }}>
            <li>✉ {summary.invited} invited</li>
            <li>– {summary.no_user_skipped} skipped (send_invite=false)</li>
            <li>✗ {summary.errors.length} error(s)</li>
          </ul>
          {summary.errors.length > 0 && (
            <div style={{
              maxHeight: 180, overflowY: 'auto',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 6, padding: 8, fontSize: 11,
            }}>
              {summary.errors.map((e, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  Row {e.row} ({e.email}): {e.reason}
                </div>
              ))}
            </div>
          )}
          <ModalFooter
            onCancel={onClose}
            submitting={false}
            onSubmit={onClose}
            submitLabel="Done"
            submitDisabled={false}
          />
        </>
      )}
    </ModalShell>
  );
}

// ── Bulk keyfobs CSV upload (Phase 3a Item 2) ────────────────

/** Data rows in a CSV, for the "N keyfobs · $X" estimate before upload:
 *  non-empty lines minus the header. The server decides what is
 *  actually issuable (and billed); this only sizes the confirm line. */
function countCsvDataRows(text: string): number {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  return Math.max(0, lines.length - 1);
}

function BulkKeyfobsModal({
  fleetId, price, onGoToBilling, onClose, onDone,
}: {
  fleetId: string;
  price: FleetKeyfobPrice | null;
  onGoToBilling: () => void;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [rowEstimate, setRowEstimate] = useState<number | null>(null);
  const [summary, setSummary] = useState<BulkKeyfobUploadSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 402 FLEET_PAST_DUE / FLEET_CARD_REQUIRED → inline prompt with the
  // Billing link (the server answers these before any charge, so nothing
  // was billed and nothing was issued).
  const [block, setBlock] = useState<FleetKeyfobBillingBlock | null>(null);
  const mut = useMutation({
    mutationFn: () => bulkUploadKeyfobs(fleetId, file!),
    onSuccess: (data) => { setSummary(data); setErr(null); onDone(); },
    onError: async (e: Error) => {
      const billing = await getFleetKeyfobBillingBlock(e);
      if (billing) { setBlock(billing); return; }
      setErr(await getDisplayErrorMessage(e, 'Upload failed.'));
    },
  });
  const pickFile = (f: File | null) => {
    setFile(f);
    setRowEstimate(null);
    if (f) {
      f.text()
        .then((txt) => setRowEstimate(countCsvDataRows(txt)))
        .catch(() => setRowEstimate(null));
    }
  };
  return (
    <ModalShell title="Bulk upload keyfobs" onClose={onClose}>
      {!summary && (
        <>
          <p style={{ fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
            CSV columns: <code>tag_uid</code> (required), <code>driver_email</code> (optional, blank = pool), <code>notes</code> (optional).
            Max 2 MB.
          </p>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            style={{ marginTop: 10 }}
          />
          <KeyfobPriceLine price={price} count={rowEstimate} />
          <KeyfobBillingPrompt block={block} price={price} onGoToBilling={onGoToBilling} />
          {err && (
            <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
              {err}
            </div>
          )}
          <ModalFooter
            onCancel={onClose}
            submitting={mut.isPending}
            onSubmit={() => { setErr(null); setBlock(null); if (file) mut.mutate(); }}
            submitLabel="Upload"
            submitDisabled={!file}
          />
        </>
      )}
      {summary && (
        <>
          <p style={{ fontSize: 13, fontWeight: 600 }}>
            Processed {summary.total_rows} row(s).
          </p>
          <ul style={{ fontSize: 12, listStyle: 'none', padding: 0, margin: '8px 0' }}>
            <li>✓ {summary.issued_pool} issued (pool)</li>
            <li>✓ {summary.issued_linked} issued (linked to driver)</li>
            <li>⚠ {summary.issued_orphaned} issued (orphaned — driver email unmatched)</li>
            <li>· {summary.already_issued} already in fleet</li>
            <li>⚠ {summary.owned_elsewhere} owned by another fleet</li>
            <li>✗ {summary.errors.length} error(s)</li>
          </ul>
          {/* The one aggregate charge for this upload; a partial refund
              means some rows were charged for and then failed to insert. */}
          <p style={{ fontSize: 12, margin: '8px 0', color: 'var(--color-tx)' }}>
            <CreditCard size={12} style={{ verticalAlign: -2, marginRight: 6 }} />
            {summary.charge.count === 0
              ? 'No keyfobs issued · no charge.'
              : keyfobChargeLine(summary.charge, summary.charge.count)}
            {summary.charge.refunded_cents > 0 && (
              <> {formatCents(summary.charge.refunded_cents)} refunded for rows that did not issue.</>
            )}
          </p>
          {summary.errors.length > 0 && (
            <div style={{
              maxHeight: 180, overflowY: 'auto',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 6, padding: 8, fontSize: 11,
            }}>
              {summary.errors.map((e, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  Row {e.row} ({e.tag_uid}): {e.reason}
                </div>
              ))}
            </div>
          )}
          <ModalFooter
            onCancel={onClose}
            submitting={false}
            onSubmit={onClose}
            submitLabel="Done"
            submitDisabled={false}
          />
        </>
      )}
    </ModalShell>
  );
}

// ── Modal scaffolding (Phase 3a) ─────────────────────────────

function ModalShell({
  title, children, onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 92vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12,
          padding: 20,
        }}
      >
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel, onSubmit, submitting, submitLabel, submitDisabled,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
  submitDisabled: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
      <button type="button" onClick={onCancel} disabled={submitting}
        style={{
          padding: '8px 14px', background: 'transparent',
          border: '1px solid var(--color-brd, var(--evi-border))',
          color: 'var(--color-tx)', borderRadius: 8, fontSize: 13, cursor: 'pointer',
        }}>
        Cancel
      </button>
      <button type="button" onClick={onSubmit}
        disabled={submitting || submitDisabled}
        style={{
          padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)',
          color: '#000', border: 'none', borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          opacity: submitting || submitDisabled ? 0.6 : 1,
        }}>
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}

// ── Shared single-input modal helper ──────────────────────

function SimpleEntryModal({
  title, label, placeholder,
  value, onChange,
  err,
  onClose, onSubmit, submitting,
}: {
  title: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  err: string | null;
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(440px, 92vw)', background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{title}</h2>
        <label className={formStyles.label}>{label}</label>
        <input
          className={formStyles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {err && (
          <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
            {err}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={submitting}
            style={{
              padding: '8px 14px', background: 'transparent',
              border: '1px solid var(--color-brd, var(--evi-border))',
              color: 'var(--color-tx)', borderRadius: 8, fontSize: 13, cursor: 'pointer',
            }}>
            Cancel
          </button>
          <button type="button" onClick={onSubmit}
            disabled={submitting || !value.trim()}
            style={{
              padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Groups tab (Phase 8b) ────────────────────────────────────────────────
//
// Renders the tenant-wide Groups list filtered down to "groups touching
// this fleet" — i.e., groups whose charger_ids overlap the fleet's
// chargers OR whose location_ids overlap the fleet's locations. Groups
// stay tenant-scoped in v1 per the Phase 8a design note; this tab is a
// convenience surface inside Fleet Detail that lets the operator manage
// the relevant subset without navigating to a separate Groups page.
//
// The filter happens client-side because the existing /admin/groups
// endpoint is tenant-scoped and adding a fleet-filter query param
// would touch the existing Groups admin page (out of scope here). At
// typical fleet sizes (<20 chargers + <50 groups) the filter is cheap.

function GroupsTab({ fleetId }: { fleetId: string }) {
  const qc = useQueryClient();
  // Tenant-wide groups list. The filter step below restricts to the
  // groups touching this fleet's chargers/locations.
  const groupsQ = useQuery({
    queryKey: ['admin', 'groups'],
    queryFn: fetchAdminGroups,
  });
  // Fleet's chargers + locations for the filter. Both tenant-scoped
  // and bounded; the count stays modest in v1.
  const chargersQ = useQuery({
    queryKey: ['admin', 'fleet-chargers', fleetId],
    queryFn: async () => {
      const { fetchChargers } = await import('@/lib/api');
      // No server-side fleet_id filter on /chargers — fetch tenant
      // chargers (capped at 500) and filter client-side. Same
      // pattern the Charger Detail Fleet Assignment dropdown uses.
      return fetchChargers({ limit: 500 });
    },
  });
  const locationsQ = useQuery({
    queryKey: ['admin', 'locations-for-groups'],
    queryFn: async () => {
      const { fetchLocations } = await import('@/lib/api');
      return fetchLocations({ limit: 500 });
    },
  });

  const fleetChargerIds = useMemo(() => {
    return new Set(
      (chargersQ.data?.data ?? [])
        .filter((c) => c.fleet_id && String(c.fleet_id) === fleetId)
        .map((c) => c._id),
    );
  }, [chargersQ.data, fleetId]);

  const fleetLocationIds = useMemo(() => {
    // Locations are tenant-scoped; a fleet's "locations" are the
    // distinct location_ids of its chargers. Same derivation the
    // server uses (no Location.fleet_id field exists).
    return new Set(
      (chargersQ.data?.data ?? [])
        .filter((c) => c.fleet_id && String(c.fleet_id) === fleetId)
        .map((c) => c.location_id),
    );
  }, [chargersQ.data, fleetId]);

  // Fleet-touching groups: any group whose scope arrays intersect
  // the fleet's chargers OR locations. Groups with empty scope
  // arrays (pure-segmentation customer groups) are excluded — they
  // don't gate this fleet's access in any meaningful way.
  const fleetGroupRows = useMemo(() => {
    return (groupsQ.data ?? []).filter((g) => {
      const c = g.charger_count ?? 0;
      const l = g.location_count ?? 0;
      return c > 0 || l > 0;
      // NOTE: Without the full charger_ids/location_ids on the
      // list endpoint (they only ship on detail) we can't precisely
      // filter to "intersects this fleet's chargers" here. v1 shows
      // all scope-bearing tenant groups and the edit modal makes
      // the chosen overlap obvious. A future endpoint extension
      // (?fleet_id= filter on /admin/groups) would tighten this.
    });
  }, [groupsQ.data]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const del = useMutation({
    mutationFn: (id: string) => deleteAdminGroup(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 12,
      }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-tx-m)' }}>
          Groups link drivers to specific fleet chargers or locations for
          granular access control within the fleet. Empty group list →
          fleet-wide access (every driver can use every fleet charger).
        </p>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', background: 'var(--evi-accent, #2ECC71)',
            color: '#000', border: 'none', borderRadius: 8,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Plus size={12} /> New group
        </button>
      </div>

      {groupsQ.isLoading && <p>Loading groups…</p>}
      {groupsQ.error && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          Failed to load groups.
        </p>
      )}

      {groupsQ.data && fleetGroupRows.length === 0 && (
        <div style={{
          padding: 24, textAlign: 'center',
          color: 'var(--color-tx-m)', fontSize: 13,
          border: '1px dashed var(--color-brd, var(--evi-border))',
          borderRadius: 10,
        }}>
          No scope-bearing groups in this tenant yet. Create one to
          restrict driver access to a subset of this fleet's chargers
          or locations.
        </div>
      )}

      {fleetGroupRows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-tx-m)' }}>
              <th style={{ padding: '8px 4px' }}>Name</th>
              <th style={{ padding: '8px 4px' }}>Drivers</th>
              <th style={{ padding: '8px 4px' }}>Chargers</th>
              <th style={{ padding: '8px 4px' }}>Locations</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fleetGroupRows.map((g) => (
              <tr key={g._id} style={{ borderTop: '1px solid var(--color-brd, var(--evi-border))' }}>
                <td style={{ padding: '8px 4px', fontWeight: 600 }}>{g.name}</td>
                <td style={{ padding: '8px 4px' }}>{g.member_count}</td>
                <td style={{ padding: '8px 4px' }}>{g.charger_count ?? 0}</td>
                <td style={{ padding: '8px 4px' }}>{g.location_count ?? 0}</td>
                <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => setEditingId(g._id)}
                    style={{
                      padding: '4px 10px', background: 'transparent',
                      border: '1px solid var(--color-brd, var(--evi-border))',
                      color: 'var(--color-tx)', borderRadius: 6,
                      fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Delete group "${g.name}"?`)) void del.mutate(g._id);
                    }}
                    style={{
                      marginLeft: 6, padding: '4px 10px', background: 'transparent',
                      border: '1px solid var(--evi-error, #ef4444)',
                      color: 'var(--evi-error, #ef4444)', borderRadius: 6,
                      fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createOpen && (
        <GroupEditModal
          mode="create"
          fleetChargerIds={fleetChargerIds}
          fleetLocationIds={fleetLocationIds}
          allChargers={chargersQ.data?.data ?? []}
          allLocations={locationsQ.data?.data ?? []}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
          }}
        />
      )}
      {editingId && (
        <GroupEditModal
          mode="edit"
          groupId={editingId}
          fleetChargerIds={fleetChargerIds}
          fleetLocationIds={fleetLocationIds}
          allChargers={chargersQ.data?.data ?? []}
          allLocations={locationsQ.data?.data ?? []}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            void qc.invalidateQueries({ queryKey: ['admin', 'groups'] });
          }}
        />
      )}
    </div>
  );
}

/**
 * Group create / edit modal. Renders three multi-selects (drivers,
 * chargers, locations) backed by the fetched tenant lists. The
 * charger + location lists are pre-narrowed to THIS fleet's chargers
 * + locations — the operator's intent here is fleet-scoped, and
 * showing every tenant charger would invite cross-fleet scope
 * accidents.
 *
 * Driver selector reuses the existing UserTypeahead pattern would
 * be ideal here, but for v1 a simple checkbox list off the existing
 * fleet drivers list keeps the modal self-contained and avoids
 * extracting a new shared driver-typeahead. Filed as a follow-up
 * if the typical group size grows past ~10 drivers.
 */
function GroupEditModal({
  mode, groupId, fleetChargerIds, fleetLocationIds,
  allChargers, allLocations, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  groupId?: string;
  fleetChargerIds: Set<string>;
  fleetLocationIds: Set<string>;
  allChargers: Array<{ _id: string; identity: string; fleet_id?: string | null }>;
  allLocations: Array<{ _id: string; name: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existingQ = useQuery({
    queryKey: ['admin', 'group', groupId],
    queryFn: () => fetchAdminGroup(groupId!),
    enabled: mode === 'edit' && Boolean(groupId),
  });
  const existing: GroupDetailView | undefined = existingQ.data;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [driverIds, setDriverIds] = useState<string[]>([]);
  const [chargerIds, setChargerIds] = useState<string[]>([]);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(mode === 'create');

  // Hydrate from existing group doc on first successful fetch.
  useMemo(() => {
    if (mode === 'edit' && existing && !hydrated) {
      setName(existing.name);
      setDescription(existing.description);
      setDriverIds(existing.members.map((m) => m._id));
      setChargerIds(existing.charger_ids ?? []);
      setLocationIds(existing.location_ids ?? []);
      setHydrated(true);
    }
    return null;
  }, [existing, hydrated, mode]);

  const fleetChargers = useMemo(
    () => allChargers.filter((c) => fleetChargerIds.has(c._id)),
    [allChargers, fleetChargerIds],
  );
  const fleetLocations = useMemo(
    () => allLocations.filter((l) => fleetLocationIds.has(l._id)),
    [allLocations, fleetLocationIds],
  );

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim(),
        driver_user_ids: driverIds,
        charger_ids: chargerIds,
        location_ids: locationIds,
      };
      if (mode === 'create') {
        return createAdminGroup(body);
      }
      return updateAdminGroup(groupId!, body);
    },
    onSuccess: () => onSaved(),
    onError: (e: Error) => setErr(e.message || 'Save failed'),
  });

  const toggleId = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    id: string,
  ) => {
    setter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(620px, 96vw)',
          maxHeight: '92vh', overflowY: 'auto',
          background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12, padding: 20,
        }}
      >
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>
          {mode === 'create' ? 'New group' : `Edit group${existing ? `: ${existing.name}` : ''}`}
        </h2>

        {mode === 'edit' && existingQ.isLoading && !hydrated && (
          <p>Loading group…</p>
        )}

        <label className={formStyles.label}>Name</label>
        <input
          className={formStyles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Night-shift drivers"
        />

        <label className={formStyles.label} style={{ marginTop: 10 }}>
          Description (optional)
        </label>
        <input
          className={formStyles.input}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <label className={formStyles.label} style={{ marginTop: 14 }}>
          Chargers in this fleet ({fleetChargers.length})
        </label>
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', margin: '0 0 6px' }}>
          Drivers in this group can use any charger checked here. Leave
          empty to grant access via location scope instead.
        </p>
        <div style={{
          maxHeight: 160, overflowY: 'auto',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 6, padding: 8,
        }}>
          {fleetChargers.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--color-tx-m)', margin: 0 }}>
              No chargers assigned to this fleet yet. Assign chargers via
              Charger Detail → Fleet Assignment.
            </p>
          )}
          {fleetChargers.map((c) => (
            <label key={c._id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 0', fontSize: 12,
            }}>
              <input
                type="checkbox"
                checked={chargerIds.includes(c._id)}
                onChange={() => toggleId(setChargerIds, c._id)}
              />
              <code>{c.identity}</code>
            </label>
          ))}
        </div>

        <label className={formStyles.label} style={{ marginTop: 14 }}>
          Locations in this fleet ({fleetLocations.length})
        </label>
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', margin: '0 0 6px' }}>
          Granting at the location level covers all of that location's
          fleet chargers. Charger + location grants are unioned.
        </p>
        <div style={{
          maxHeight: 140, overflowY: 'auto',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 6, padding: 8,
        }}>
          {fleetLocations.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--color-tx-m)', margin: 0 }}>
              No locations host this fleet's chargers yet.
            </p>
          )}
          {fleetLocations.map((l) => (
            <label key={l._id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 0', fontSize: 12,
            }}>
              <input
                type="checkbox"
                checked={locationIds.includes(l._id)}
                onChange={() => toggleId(setLocationIds, l._id)}
              />
              {l.name}
            </label>
          ))}
        </div>

        <label className={formStyles.label} style={{ marginTop: 14 }}>
          Drivers ({driverIds.length})
        </label>
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', margin: '0 0 6px' }}>
          Driver multi-select v1 — comma-separated user ObjectIds.
          Copy ids from the Drivers tab. A future commit will extract
          a shared UserTypeahead variant for fleet-scoped picking.
        </p>
        <textarea
          value={driverIds.join(',')}
          onChange={(e) => setDriverIds(
            e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
          )}
          rows={3}
          style={{
            width: '100%', padding: 8, fontSize: 12, fontFamily: 'monospace',
            background: 'var(--color-sf2, #1a1a1a)',
            border: '1px solid var(--color-brd, var(--evi-border))',
            color: 'var(--color-tx)', borderRadius: 6,
            boxSizing: 'border-box',
          }}
        />

        {err && (
          <div style={{ marginTop: 10, color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={save.isPending}
            style={{
              padding: '8px 14px', background: 'transparent',
              border: '1px solid var(--color-brd, var(--evi-border))',
              color: 'var(--color-tx)', borderRadius: 8,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { setErr(null); save.mutate(); }}
            disabled={save.isPending || !name.trim()}
            style={{
              padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              opacity: save.isPending || !name.trim() ? 0.6 : 1,
            }}
          >
            {save.isPending ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
